/**
 * IdmsAnalyzeView — IDMS-specific Analyze Workspace
 *
 * Nine tabs:
 *   Inventory .......... Grouped asset table + detail panel + AI Actions
 *   DML Operations ..... Operations table with SQL/Java patterns
 *   Schema ............. Three-panel view: Sets | Records | Areas
 *   Data Architecture .. Interactive ERD (ReactFlow)
 *   Copybooks .......... Field tree + raw COBOL viewer
 *   Compatibility ...... Findings + Transform (toggle)
 *   Assessment ......... Executive readiness dashboard
 *   V6 Output .......... V6 COBOL source + change/JCL/DML reports
 *   SQL Generation ..... Tree-based SQL browser
 *
 * Data sources (BLL IDMS endpoints):
 *   /idmsanalysis/latest-job           → discover analysis job
 *   /idmsanalysis/{id}/results/json/program_results.json
 *   /idmsanalysis/{id}/results/json/dml_operations.json
 *   /idmsanalysis/{id}/results/json/schema_inventory.json
 *   /idmsanalysis/{id}/results/json/copybooks.json
 *   /idmsanalysis/{id}/results/json/tree_sitter_results.json
 *   /idmscompatibility/{id}/results/json/compatibility_results.json
 *   /idmscompatibility/{id}/results/json/cmpr2_findings.json
 *   /idmscompatibility/{id}/results/json/transform_changes.json
 *   /idmscompatibility/{id}/results/json/v6_output_manifest.json
 *   /idmsdataarchitecture/{id}/results/json/inferred_schema.json
 *   /idmsdataarchitecture/{id}/results/json/analysis_graph.json
 *   /idmsassessment/{id}/results/json/assessment_full.json
 *   /idmsassessment/{id}/results/json/program_risks.json
 *   /idmssqlgeneration/{id}/results/json/sql_tables.json
 *   /idmssqlgeneration/{id}/results/json/sql_procedures.json
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  ArrowLeft, ChevronRight, ChevronDown, Search,
  Loader2, BarChart2, Home, FileBarChart,
  Settings, HelpCircle, User, Database,
  Code2, Layers, GitBranch, Network, Shield,
  AlertTriangle, FileCode, Download, Eye,
  CheckCircle2, XCircle, AlertCircle, Copy,
  BookOpen, MoreVertical, Filter,
} from 'lucide-react'
import { ReactFlow, Background, Controls, MiniMap, ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { getAPIBaseUrl } from '../../environment'
import './GuidedAnalyzeView.css'
import '../runs/RunsView.css'
import AIResponseModal from '../editor/AIResponseModal'
import { explainCobolCode, customPrompt as customPromptAI } from '../../services/aiService'
import JSZip from 'jszip'

// ── Constants ─────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'inventory',     label: 'Inventory',          icon: FileBarChart },
  { key: 'dml',           label: 'DML Operations',     icon: Database },
  { key: 'schema',        label: 'Schema',             icon: Layers },
  { key: 'architecture',  label: 'Data Architecture',  icon: Network },
  { key: 'copybooks',     label: 'Copybooks',          icon: FileCode },
  { key: 'compatibility', label: 'Compatibility',      icon: Shield },
  { key: 'assessment',    label: 'Assessment',         icon: BarChart2 },
  { key: 'v6output',      label: 'V6 Output',          icon: Eye },
  { key: 'sql',           label: 'SQL Generation',     icon: Code2 },
]

const IDMS_GROUP_ORDER  = ['IDMS PROGRAMS', 'NON-IDMS', 'COPYBOOKS']
const IDMS_GROUP_COLORS = {
  'IDMS PROGRAMS': '#388bfd',
  'NON-IDMS':      '#6b7280',
  'COPYBOOKS':     '#a78bfa',
}

const IDMS_SCOPE_CHIPS = [
  { key: 'all',       label: 'All' },
  { key: 'idms',      label: 'IDMS Only' },
  { key: 'non-idms',  label: 'Non-IDMS' },
  { key: 'copybooks', label: 'Copybooks' },
]

const IDMS_AI_ACTIONS = [
  { id: 'custom-prompt',      label: 'Custom Prompt',         icon: Code2 },
  { id: 'explain-idms',       label: 'Explain IDMS Usage',    icon: BookOpen },
  { id: 'map-dml-sql',        label: 'Map DML to SQL',        icon: Database },
  { id: 'business-use-case',  label: 'Business Use Case',     icon: BarChart2 },
  { id: 'assess-migration',   label: 'Assess Migration Risk', icon: Shield },
]

const IDMS_ACTION_TITLE = {
  'custom-prompt':      'Custom Prompt',
  'explain-idms':       'Explain IDMS Usage',
  'map-dml-sql':        'Map DML to SQL',
  'business-use-case':  'Business Use Case',
  'assess-migration':   'Assess Migration Risk',
}

const IDMS_ACTION_MODE = {
  'custom-prompt':      'custom',
  'explain-idms':       'explain',
  'map-dml-sql':        'explain',
  'business-use-case':  'explain',
  'assess-migration':   'explain',
}

const IDMS_RECOMMENDATIONS = {
  'idms-program': [
    'Map OBTAIN/FIND DML verbs to equivalent SQL SELECT patterns.',
    'Identify set navigation (CONNECT/DISCONNECT) for relational foreign key design.',
    'Review STORE/MODIFY/ERASE operations for INSERT/UPDATE/DELETE mapping.',
    'Extract subschema-bound data areas into Java entity classes.',
    'Analyze IDMS error handling (DB-STATUS) for exception mapping.',
  ],
  'cobol': [
    'Extract shared data items into separate Java model classes.',
    'Review PERFORM loops for conversion to Java iterators.',
    'Validate COMP-3 packed-decimal fields against Java BigDecimal.',
  ],
  'copybook': [
    'Map copybook field definitions to Java entity attributes.',
    'Review redefined fields for proper type hierarchy.',
    'Check field lengths for JPA column size annotations.',
  ],
}

function computeIdmsComplexity(file) {
  if (!file || file.type === 'copybook') return null
  if (!file.lines || file.lines === 0) return null  // no line data → can't compute
  const lpp = file.paragraphs > 0 ? file.lines / file.paragraphs : file.lines
  return lpp > 60 ? 'High' : lpp > 20 ? 'Medium' : 'Low'
}

function computeIdmsRiskMetrics(file) {
  if (!file || file.type === 'copybook') return null
  const dmlDensity     = file.lines > 0 ? (file.dmlCount / file.lines) * 100 : 0
  const recordCoupling = (file.copyIdmsRecords || []).length
  const setCoupling    = (file.setsReferenced || []).length
  const areaSpread     = (file.areasReferenced || []).length
  const lpp            = file.paragraphs > 0 ? file.lines / file.paragraphs : file.lines
  const fanOutCalls    = file.callStatements || 0
  const fanOutCopybooks = (file.copybooksUsed || []).length

  // Risk score (0-100): higher DML count, more records/sets/areas = harder migration
  const dmlCount = file.dmlCount || 0
  let score = 0
  score += Math.min(dmlCount / 5, 25)          // Raw DML count: up to 25 (125+ ops = max)
  score += Math.min(recordCoupling * 2, 25)    // Records: up to 25
  score += Math.min(setCoupling * 3, 20)       // Sets: up to 20
  score += Math.min(areaSpread * 5, 15)        // Areas: up to 15
  score += Math.min(lpp / 5, 15)               // Structural complexity: up to 15
  score = Math.round(Math.min(score, 100))

  return {
    dmlDensity:       dmlDensity.toFixed(1),
    recordCoupling,
    setCoupling,
    areaSpread,
    linesPerParagraph: Math.round(lpp),
    fanOut:           fanOutCalls + fanOutCopybooks,
    fanOutCalls,
    fanOutCopybooks,
    riskScore:        score,
    primarySignal:    score >= 70 ? 'High IDMS coupling — complex migration path'
                    : score >= 40 ? 'Moderate complexity — standard migration effort'
                    : 'Low coupling — straightforward migration',
  }
}

const DML_CATEGORY_COLORS = {
  lifecycle:     { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  retrieval:     { color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
  navigation:    { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
  modification:  { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  currency:      { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
  error_handling:{ color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  other:         { color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
}

const SEVERITY_COLORS = {
  silent_runtime_bug: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', label: 'Runtime Bug' },
  behavioral_change:  { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Behavioral' },
  compile_error:      { color: '#f97316', bg: 'rgba(249,115,22,0.12)', label: 'Compile Error' },
  warning:            { color: '#6b7280', bg: 'rgba(107,114,128,0.12)', label: 'Warning' },
  info:               { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', label: 'Info' },
}

// ── Schema Explorer constants ────────────────────────────────────────────────

const SCHEMA_GROUP_ORDER  = ['RECORDS', 'SETS', 'AREAS']
const SCHEMA_GROUP_COLORS = {
  RECORDS: '#3b82f6',
  SETS:    '#10b981',
  AREAS:   '#f59e0b',
}

const SCHEMA_SCOPE_CHIPS = [
  { key: 'all',     label: 'All' },
  { key: 'records', label: 'Records' },
  { key: 'sets',    label: 'Sets' },
  { key: 'areas',   label: 'Areas' },
]

const VERB_COLORS = {
  OBTAIN:     '#10b981',
  FIND:       '#3b82f6',
  GET:        '#06b6d4',
  STORE:      '#f59e0b',
  MODIFY:     '#8b5cf6',
  ERASE:      '#ef4444',
  CONNECT:    '#f97316',
  DISCONNECT: '#fb923c',
  BIND:       '#6b7280',
  READY:      '#9ca3af',
  FINISH:     '#d1d5db',
  ACCEPT:     '#14b8a6',
  RETURN:     '#a78bfa',
  IF:         '#fbbf24',
}

// ── Copybooks tab constants ──────────────────────────────────────────────────

const CB_SCOPE_CHIPS = [
  { key: 'all',    label: 'All' },
  { key: 'idms',   label: 'IDMS Records' },
  { key: 'work',   label: 'Working Storage' },
  { key: 'infra',  label: 'Infrastructure' },
]

const CB_TYPE_COLORS = {
  'IDMS Record':      '#3b82f6',
  'Working Storage':  '#8b5cf6',
  'Infrastructure':   '#6b7280',
}

const PIC_TYPE_MAP = [
  { pattern: /^S?9.*V9.*COMP-3$/i,  sql: 'DECIMAL' },
  { pattern: /^S?9.*V/i,            sql: 'DECIMAL' },
  { pattern: /COMP-1$/i,            sql: 'REAL' },
  { pattern: /COMP-2$/i,            sql: 'DOUBLE' },
  { pattern: /^S?9.*COMP-3$/i,      sql: 'DECIMAL' },
  { pattern: /^S?9.*(COMP|BINARY)$/i, sql: 'INTEGER' },
  { pattern: /^S?9/,                sql: 'INTEGER' },
  { pattern: /^X\(1\)$|^X$/,        sql: 'CHAR(1)' },
  { pattern: /^X/,                  sql: 'VARCHAR' },
]

function picToSqlType(pic, usage) {
  if (!pic) return null
  const combined = usage ? `${pic} ${usage}` : pic
  for (const m of PIC_TYPE_MAP) {
    if (m.pattern.test(combined)) return m.sql
  }
  return 'VARCHAR'
}

function classifyCopybook(cb, schemaRecords) {
  const name = cb.name || ''
  if (schemaRecords && schemaRecords.includes(name)) return 'IDMS Record'
  if (/^(PGM|PBWK|LS-|PSVMAHPA|PSVNHPNA)/.test(name)) return 'Working Storage'
  if (/^(PGM-|LEAF-NODE)/.test(name)) return 'Infrastructure'
  return 'IDMS Record'  // default — most IDMS copybooks are record layouts
}

function flattenFields(fields, depth) {
  if (!fields || !fields.length) return []
  const result = []
  const d = depth || 0
  for (const f of fields) {
    result.push({ ...f, _depth: d })
    if (f.children && f.children.length) {
      result.push(...flattenFields(f.children, d + 1))
    }
  }
  return result
}

function parseFieldsFromRawCobol(rawCobol) {
  if (!rawCobol) return []
  const lines = rawCobol.split('\n')
  const fields = []
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('*')) continue
    const m = trimmed.match(/^(\d{2})\s+([\w-]+)(?:\s+PIC\s+(\S+?)\.?)?(?:\s+(COMP-\d|BINARY|PACKED-DECIMAL))?/)
    if (m) {
      const lvl = parseInt(m[1])
      fields.push({
        level: lvl,
        name: m[2].replace(/\.$/, ''),
        pic: (m[3] || '').replace(/\.$/, ''),
        usage: m[4] || '',
        byte_offset: null,
        byte_size: null,
        redefines: /REDEFINES/i.test(trimmed),
        occurs: (/OCCURS\s*\(?\s*(\d+)/i.exec(trimmed) || [])[1] ? parseInt((/OCCURS\s*\(?\s*(\d+)/i.exec(trimmed))[1]) : 0,
        children: [],
        _depth: Math.max(0, Math.floor((lvl - 1) / 4)),
      })
    }
  }
  return fields
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const API_BASE = window.__MODERNIZEIT_API_URL__ || getAPIBaseUrl()

function getAuthHeaders() {
  const token = localStorage.getItem('modernizeit_token') || ''
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function fetchJson(url) {
  const r = await fetch(url, { headers: getAuthHeaders() })
  if (!r.ok) return null
  return r.json()
}

async function fetchLatestJob(endpoint, accountId, appName) {
  const account = encodeURIComponent(accountId || '')
  const app     = encodeURIComponent(appName || '')
  const data = await fetchJson(`${API_BASE}/${endpoint}/latest-job?account_id=${account}&app_name=${app}`)
  return data?.job_id || null
}

async function fetchArtifact(endpoint, jobId, filename) {
  if (!jobId) return null
  return fetchJson(`${API_BASE}/${endpoint}/${jobId}/results/json/${filename}`)
}

// ── Badge Components ──────────────────────────────────────────────────────────

function CategoryBadge({ category }) {
  const c = DML_CATEGORY_COLORS[category] || DML_CATEGORY_COLORS.other
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: 11, fontWeight: 600, color: c.color, backgroundColor: c.bg,
    }}>
      {category}
    </span>
  )
}

function SeverityBadge({ severity }) {
  const s = SEVERITY_COLORS[severity] || SEVERITY_COLORS.info
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: 11, fontWeight: 600, color: s.color, backgroundColor: s.bg,
    }}>
      {s.label}
    </span>
  )
}

function ScoreBadge({ score, label }) {
  const color = score >= 80 ? '#3fb950' : score >= 50 ? '#f59e0b' : '#ef4444'
  const bg    = score >= 80 ? 'rgba(63,185,80,0.12)' : score >= 50 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 10,
      fontSize: 12, fontWeight: 600, color, backgroundColor: bg,
    }}>
      {label && <span style={{ fontWeight: 400 }}>{label}</span>}
      {score}%
    </span>
  )
}

// ── Not Available Placeholder ──────────────────────────────────────────────

function NotAvailable({ message }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 60, color: 'var(--text-muted)', textAlign: 'center', gap: 8,
    }}>
      <AlertCircle size={32} style={{ opacity: 0.4 }} />
      <p style={{ margin: 0, fontSize: 14 }}>{message || 'This pipeline step has not run yet.'}</p>
      <p style={{ margin: 0, fontSize: 12, opacity: 0.6 }}>Run the IDMS pipeline to generate this data.</p>
    </div>
  )
}

// ── Section Header ────────────────────────────────────────────────────────────

function SectionHeader({ children }) {
  return (
    <h3 style={{
      margin: '20px 0 10px', fontSize: 13, fontWeight: 600,
      color: 'var(--text-secondary)', textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}>
      {children}
    </h3>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Inventory
// ═══════════════════════════════════════════════════════════════════════════════

function buildIdmsInventory(programResults, copybooks, dmlOps) {
  const files = []
  let id = 1

  // Programs from program_results.json (dict keyed by name)
  const programs = (programResults && typeof programResults === 'object' && !Array.isArray(programResults))
    ? Object.entries(programResults) : []

  for (const [name, prog] of programs) {
    const idms = prog.idms || {}
    const listing = prog.listing || {}
    const ts = prog.tree_sitter || {}
    const isIdms = (idms.operation_count > 0) || !!idms.subschema
    files.push({
      id: id++,
      name: prog.filename || name,
      path: prog.filename || name,
      group: isIdms ? 'IDMS PROGRAMS' : 'NON-IDMS',
      type: isIdms ? 'idms-program' : 'cobol',
      lines: listing.total_lines || listing.code_lines || 0,
      dmlCount: idms.operation_count || 0,
      subschema: idms.subschema || null,
      idmsMode: idms.idms_mode || null,
      sourceForm: idms.source_form || null,
      copyIdmsRecords: idms.copy_idms_records || [],
      setsReferenced: idms.sets_referenced || [],
      areasReferenced: idms.areas_referenced || [],
      paragraphs: Array.isArray(ts.paragraphs) ? ts.paragraphs.length : 0,
      performCalls: Array.isArray(ts.perform_calls) ? ts.perform_calls.length : 0,
      callStatements: Array.isArray(ts.call_statements) ? ts.call_statements.length : 0,
      copybooksUsed: ts.copybooks_used || [],
      listing,
      idms,
      treeSitter: ts,
    })
  }

  // Copybooks from copybooks.json (flat array)
  const cbs = Array.isArray(copybooks) ? copybooks : []
  for (const cb of cbs) {
    files.push({
      id: id++,
      name: cb.name || cb.copybook_name || 'Unknown',
      path: cb.name || cb.copybook_name || '',
      group: 'COPYBOOKS',
      type: 'copybook',
      lines: cb.total_lines || (cb.fields || []).length || 0,
      dmlCount: 0,
      fieldCount: (cb.fields || []).length,
      verified: cb.verified || false,
    })
  }

  return files
}


function IdmsDetailPanel({ file, onClose, ingestJobId }) {
  const [filePreview, setFilePreview] = useState(null)
  const [fullModal, setFullModal]     = useState(false)

  // Fetch file content for preview when selection changes
  useEffect(() => {
    setFilePreview(null)
    setFullModal(false)
    if (!file) return

    if (file.type === 'copybook') {
      setFilePreview({ loading: false, content: null, error: 'Copybook source preview not available.' })
      return
    }
    if (!ingestJobId) {
      setFilePreview({ loading: false, content: null, error: 'Preview requires file catalog — ingest job not found.' })
      return
    }

    setFilePreview({ loading: true, content: null, error: null })
    const url = `${API_BASE}/editor/cobol/file?job_id=${encodeURIComponent(ingestJobId)}&path=${encodeURIComponent(file.path)}`
    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const content = data.content || ''
        setFilePreview({ loading: false, content: content || null, error: content ? null : 'File content is empty.' })
      })
      .catch(err => {
        setFilePreview({ loading: false, content: null, error: `Could not load preview: ${err.message}` })
      })
  }, [file?.id, ingestJobId])

  if (!file) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', color: 'var(--text-muted)', fontSize: 13,
      }}>
        Select a file to view details
      </div>
    )
  }

  const isCopybook = file.type === 'copybook'
  const isIdms     = file.type === 'idms-program'
  const complexity = computeIdmsComplexity(file)
  const rm         = computeIdmsRiskMetrics(file)
  const recs       = IDMS_RECOMMENDATIONS[file.type] || IDMS_RECOMMENDATIONS['cobol']

  const DetailRow = ({ label, value }) => value != null && value !== '' ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontFamily: 'monospace', textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </span>
    </div>
  ) : null

  const DetailSection = ({ title, children }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: 6, paddingBottom: 4,
        borderBottom: '1px solid var(--border-color)',
      }}>
        {title}
      </div>
      {children}
    </div>
  )

  const TagList = ({ items, color }) => {
    if (!items || items.length === 0) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>None</span>
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
        {items.map((item, i) => (
          <span key={i} style={{
            padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, fontFamily: 'monospace',
            color: color, backgroundColor: `${color}18`,
          }}>
            {typeof item === 'string' ? item : item.name || JSON.stringify(item)}
          </span>
        ))}
      </div>
    )
  }

  const cxColor = complexity === 'High' ? '#ef4444' : complexity === 'Medium' ? '#f59e0b' : '#10b981'
  const typeLabel = isCopybook ? 'Copybook' : isIdms ? 'IDMS Program' : 'COBOL Program'

  return (
    <div style={{ padding: 16, overflowY: 'auto', height: '100%' }}>
      {/* Header — file name + group badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {file.name}
          </div>
          <span style={{
            display: 'inline-block', marginTop: 4, padding: '1px 8px', borderRadius: 10,
            fontSize: 10, fontWeight: 600,
            color: IDMS_GROUP_COLORS[file.group] || '#6b7280',
            backgroundColor: `${IDMS_GROUP_COLORS[file.group] || '#6b7280'}18`,
          }}>
            {file.group}
          </span>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4,
        }}>
          <XCircle size={16} />
        </button>
      </div>

      {/* Structured metadata */}
      <div style={{ marginBottom: 16 }}>
        <DetailRow label="TYPE" value={typeLabel} />
        {isIdms && <DetailRow label="SUBSCHEMA" value={file.subschema || '—'} />}
        {isIdms && <DetailRow label="MODE" value={file.idmsMode || '—'} />}
        {isIdms && <DetailRow label="SOURCE FORM" value={file.sourceForm || '—'} />}
        <DetailRow label="DML OPERATIONS" value={isIdms ? file.dmlCount : (isCopybook ? '—' : 0)} />
        <DetailRow label="LINE COUNT" value={file.lines > 0 ? file.lines.toLocaleString() : '—'} />
        {isCopybook && <DetailRow label="FIELDS" value={file.fieldCount || 0} />}
        {/* Complexity badge */}
        {complexity && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)' }}>COMPLEXITY</span>
            <span style={{
              padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
              color: cxColor, background: `${cxColor}18`,
            }}>
              {complexity}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
          <span style={{ color: 'var(--text-muted)' }}>STATUS</span>
          <span style={{
            padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
            color: '#10b981', background: '#10b98118',
          }}>
            Classified
          </span>
        </div>
      </div>

      {/* ── RISK ANALYSIS ─────────────────────────────────────────── */}
      {rm && (
        <DetailSection title="Risk Analysis">
          {/* Risk score badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, fontFamily: 'monospace',
              color: rm.riskScore >= 70 ? '#ef4444' : rm.riskScore >= 40 ? '#f59e0b' : '#10b981',
              border: `2px solid ${rm.riskScore >= 70 ? '#ef4444' : rm.riskScore >= 40 ? '#f59e0b' : '#10b981'}`,
              background: `${rm.riskScore >= 70 ? '#ef4444' : rm.riskScore >= 40 ? '#f59e0b' : '#10b981'}12`,
              flexShrink: 0,
            }}>
              {rm.riskScore}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.3 }}>
              Migration Risk Score
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>(0 = low, 100 = high)</div>
            </div>
          </div>

          {/* DML Density */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
            <span style={{ color: 'var(--text-muted)' }}>DML Density</span>
            <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontWeight: 500 }}>
              {rm.dmlDensity} <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>ops/100 lines</span>
            </span>
          </div>

          {/* Record Coupling */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
            <span style={{ color: 'var(--text-muted)' }}>Record Coupling</span>
            <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontWeight: 500 }}>
              {rm.recordCoupling} <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>records</span>
            </span>
          </div>

          {/* Set Complexity */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
            <span style={{ color: 'var(--text-muted)' }}>Set Complexity</span>
            <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontWeight: 500 }}>
              {rm.setCoupling} <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>sets</span>
            </span>
          </div>

          {/* Area Spread */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
            <span style={{ color: 'var(--text-muted)' }}>Area Spread</span>
            <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontWeight: 500 }}>
              {rm.areaSpread} <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>areas</span>
            </span>
          </div>

          {/* Fan-out */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
            <span style={{ color: 'var(--text-muted)' }}>Fan-out</span>
            <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontWeight: 500 }}>
              {rm.fanOut}
              <span style={{ color: 'var(--text-muted)', fontSize: 10 }}> ({rm.fanOutCalls} calls, {rm.fanOutCopybooks} copybooks)</span>
            </span>
          </div>

          {/* Lines per paragraph */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
            <span style={{ color: 'var(--text-muted)' }}>Lines/Paragraph</span>
            <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontWeight: 500 }}>
              {rm.linesPerParagraph}
            </span>
          </div>

          {/* Instability bar */}
          {file.lines > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>
                <span>Migration Effort</span>
                <span>{rm.riskScore}%</span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-primary)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 2,
                  width: `${rm.riskScore}%`,
                  background: rm.riskScore >= 70 ? '#ef4444' : rm.riskScore >= 40 ? '#f59e0b' : '#10b981',
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          )}

          {/* Primary signal */}
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: 1.4 }}>
            {rm.primarySignal}
          </div>
        </DetailSection>
      )}

      {/* ── RECOMMENDATIONS ───────────────────────────────────────── */}
      <DetailSection title="Recommendations">
        <ul style={{ margin: 0, paddingLeft: 16, listStyleType: 'disc' }}>
          {recs.map((r, i) => (
            <li key={i} style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 4 }}>
              {r}
            </li>
          ))}
        </ul>
      </DetailSection>

      {/* ── IDMS Records ──────────────────────────────────────────── */}
      {isIdms && file.copyIdmsRecords && file.copyIdmsRecords.length > 0 && (
        <DetailSection title="IDMS Records">
          <TagList items={file.copyIdmsRecords} color="#3b82f6" />
        </DetailSection>
      )}

      {/* Sets & Areas Referenced */}
      {isIdms && file.setsReferenced && file.setsReferenced.length > 0 && (
        <DetailSection title="Sets Referenced">
          <TagList items={file.setsReferenced} color="#10b981" />
        </DetailSection>
      )}
      {isIdms && file.areasReferenced && file.areasReferenced.length > 0 && (
        <DetailSection title="Areas Referenced">
          <TagList items={file.areasReferenced} color="#f59e0b" />
        </DetailSection>
      )}

      {/* ── PREVIEW ───────────────────────────────────────────────── */}
      <DetailSection title="Preview">
        {filePreview?.loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
            <Loader2 size={14} className="ga-spin" style={{ color: '#388bfd' }} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Loading preview...</span>
          </div>
        )}
        {filePreview?.error && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', padding: '4px 0' }}>
            {filePreview.error}
          </div>
        )}
        {filePreview?.content && (
          <>
            <pre style={{
              fontSize: 10, fontFamily: 'monospace', color: 'var(--text-secondary)',
              background: 'var(--bg-primary)', padding: 8, borderRadius: 4,
              maxHeight: 200, overflowY: 'auto', overflowX: 'auto',
              whiteSpace: 'pre', lineHeight: 1.4, margin: 0,
              border: '1px solid var(--border-color)',
            }}>
              {filePreview.content.split('\n').slice(0, 50).join('\n')}
            </pre>
            {filePreview.content.split('\n').length > 50 && (
              <button
                onClick={() => setFullModal(true)}
                style={{
                  display: 'block', marginTop: 6, background: 'none', border: 'none',
                  cursor: 'pointer', color: '#388bfd', fontSize: 11, fontWeight: 600,
                  padding: 0,
                }}
              >
                More &rarr;
              </button>
            )}
          </>
        )}
      </DetailSection>

      {/* Listing Stats (programs only) */}
      {!isCopybook && file.listing && (
        <DetailSection title="Listing Statistics">
          <DetailRow label="Total Lines" value={file.listing.total_lines} />
          <DetailRow label="Code Lines" value={file.listing.code_lines} />
          <DetailRow label="Comment Lines" value={file.listing.comment_lines} />
          <DetailRow label="Blank Lines" value={file.listing.blank_lines} />
          <DetailRow label="Data Division" value={file.listing.data_division_lines} />
          <DetailRow label="Procedure Div" value={file.listing.procedure_division_lines} />
        </DetailSection>
      )}

      {/* Tree-sitter Analysis (programs only) */}
      {!isCopybook && (file.paragraphs > 0 || file.performCalls > 0 || file.callStatements > 0) && (
        <DetailSection title="Structure Analysis">
          <DetailRow label="Paragraphs" value={file.paragraphs} />
          <DetailRow label="PERFORM Calls" value={file.performCalls} />
          <DetailRow label="CALL Statements" value={file.callStatements} />
          {file.copybooksUsed && file.copybooksUsed.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Copybooks Used</span>
              <TagList items={file.copybooksUsed} color="#a78bfa" />
            </div>
          )}
        </DetailSection>
      )}

      {/* ── Full Source Modal ─────────────────────────────────────── */}
      {fullModal && filePreview?.content && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setFullModal(false)}>
          <div
            style={{
              background: 'var(--bg-primary)', borderRadius: 12, width: '80vw', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              border: '1px solid var(--border-color)', boxShadow: '0 12px 48px rgba(0,0,0,0.5)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', borderBottom: '1px solid var(--border-color)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Eye size={16} style={{ color: '#388bfd' }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                  {file.name}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    const blob = new Blob([filePreview.content], { type: 'text/plain' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url; a.download = file.name; a.click()
                    URL.revokeObjectURL(url)
                  }}
                  style={{
                    padding: '5px 14px', borderRadius: 6, border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                    fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <Download size={13} /> Export
                </button>
                <button
                  onClick={() => setFullModal(false)}
                  style={{
                    padding: '5px 14px', borderRadius: 6, border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                    fontSize: 12, cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            {/* Modal body */}
            <pre style={{
              flex: 1, overflow: 'auto', margin: 0, padding: 16,
              fontSize: 12, fontFamily: 'monospace', color: 'var(--text-secondary)',
              lineHeight: 1.5, whiteSpace: 'pre',
            }}>
              {filePreview.content}
            </pre>
          </div>
        </div>
      )}
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// IDMS AI Modal — mirrors InventoryAIModal from GuidedAnalyzeView
// ═══════════════════════════════════════════════════════════════════════════════

function IdmsInventoryAIModal({ action, file, ingestJobId, project, onClose }) {
  const [fileContent,    setFileContent]    = useState(null)
  const [contentLoading, setContentLoading] = useState(false)
  const [aiLoading,      setAiLoading]      = useState(false)
  const [aiResponse,     setAiResponse]     = useState(null)
  const [aiError,        setAiError]        = useState(null)
  const [autoTriggered,  setAutoTriggered]  = useState(false)
  const [saving,         setSaving]         = useState(false)
  const [saveStatus,     setSaveStatus]     = useState(null)

  // Build structured IDMS metadata context (always available, even without file content)
  const idmsContext = useMemo(() => {
    if (!file) return ''
    const parts = [`=== IDMS Program Analysis: ${file.name} ===`]
    if (file.subschema) parts.push(`Subschema: ${file.subschema}`)
    if (file.idmsMode) parts.push(`IDMS Mode: ${file.idmsMode}`)
    if (file.sourceForm) parts.push(`Source Form: ${file.sourceForm}`)
    parts.push(`DML Operations: ${file.dmlCount || 0}`)
    if (file.lines) parts.push(`Total Lines: ${file.lines}`)
    if (file.copyIdmsRecords?.length) parts.push(`IDMS Records: ${file.copyIdmsRecords.join(', ')}`)
    if (file.setsReferenced?.length) {
      parts.push(`Sets Referenced: ${file.setsReferenced.map(s => typeof s === 'string' ? s : s.name || JSON.stringify(s)).join(', ')}`)
    }
    if (file.areasReferenced?.length) {
      parts.push(`Areas Referenced: ${file.areasReferenced.map(a => typeof a === 'string' ? a : a.name || JSON.stringify(a)).join(', ')}`)
    }
    if (file.paragraphs) parts.push(`Paragraphs: ${file.paragraphs}`)
    if (file.performCalls) parts.push(`PERFORM Calls: ${file.performCalls}`)
    if (file.callStatements) parts.push(`CALL Statements: ${file.callStatements}`)
    if (file.listing) {
      const l = file.listing
      if (l.code_lines) parts.push(`Code Lines: ${l.code_lines}`)
      if (l.comment_lines) parts.push(`Comment Lines: ${l.comment_lines}`)
      if (l.data_division_lines) parts.push(`Data Division Lines: ${l.data_division_lines}`)
      if (l.procedure_division_lines) parts.push(`Procedure Division Lines: ${l.procedure_division_lines}`)
    }
    if (file.idms?.statements?.length) {
      parts.push('\n--- DML Statements ---')
      file.idms.statements.forEach(s => {
        parts.push(`  Line ${s.line_number || '?'}: ${s.verb || '?'} ${s.record_name || ''} ${s.set_name || ''}`.trim())
      })
    }
    return parts.join('\n')
  }, [file])

  // ── Fetch file content if ingest job ID available ─────────────────────────
  useEffect(() => {
    if (!ingestJobId || !file?.path) {
      setFileContent('')
      return
    }
    setContentLoading(true)
    const token   = localStorage.getItem('modernizeit_token') || ''
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    const url     = `${API_BASE}/editor/cobol/file?job_id=${encodeURIComponent(ingestJobId)}&path=${encodeURIComponent(file.path)}`

    console.log('[IdmsAI] Fetching file content for:', file.name)
    fetch(url, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => {
        const text = d?.content || ''
        console.log('[IdmsAI] File content loaded:', file.name, `${(text.length / 1024).toFixed(1)} KB`)
        setFileContent(text)
        setContentLoading(false)
      })
      .catch(err => {
        console.warn('[IdmsAI] Content fetch failed:', err.message, '— using metadata context')
        setFileContent('')
        setContentLoading(false)
      })
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-trigger AI for non-custom actions once content is ready ──────────
  useEffect(() => {
    if (action === 'custom-prompt') return
    if (autoTriggered || aiLoading || aiResponse) return
    if (contentLoading) return
    if (fileContent === null) return
    runAI()
    setAutoTriggered(true)
  }, [fileContent, contentLoading, autoTriggered, aiLoading, aiResponse])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI call dispatcher ────────────────────────────────────────────────────
  const runAI = async (userPrompt) => {
    setAiLoading(true)
    setAiResponse(null)
    setAiError(null)

    const code = fileContent || idmsContext
    const ctx  = {
      scout_account_id: project?.account,
      application_name: project?.name,
    }

    console.log('[IdmsAI] Running action:', action, 'for:', file?.name)
    try {
      let result
      if (action === 'explain-idms') {
        const prompt =
          `Analyze this COBOL/IDMS program "${file.name}" and explain its IDMS database interactions:\n` +
          `1. What IDMS subschema does it use and what does that imply about the database area?\n` +
          `2. What DML operations does it perform (OBTAIN, STORE, MODIFY, ERASE, FIND, etc.)?\n` +
          `3. What IDMS records does it access and what is the data flow?\n` +
          `4. What sets and areas does it navigate?\n` +
          `5. Summarize the overall IDMS usage pattern (navigational vs direct, batch vs online).` +
          (idmsContext ? `\n\nIDMS Metadata:\n${idmsContext}` : '')
        result = await customPromptAI(code, prompt, file.name, file.name, ctx)

      } else if (action === 'map-dml-sql') {
        const prompt =
          `Map each IDMS DML operation in program "${file.name}" to equivalent SQL statements.\n` +
          `For each DML verb (OBTAIN, STORE, MODIFY, ERASE, FIND, GET, CONNECT, DISCONNECT, etc.):\n` +
          `1. Show the original IDMS DML statement\n` +
          `2. Show the equivalent SQL (SELECT, INSERT, UPDATE, DELETE, etc.)\n` +
          `3. Explain any structural differences (set navigation -> JOIN, currency -> WHERE clause, etc.)\n` +
          `4. Note any IDMS concepts that have no direct SQL equivalent and suggest workarounds.` +
          (idmsContext ? `\n\nIDMS Metadata:\n${idmsContext}` : '')
        result = await customPromptAI(code, prompt, file.name, file.name, ctx)

      } else if (action === 'business-use-case') {
        const prompt =
          `Describe the business use case for this IDMS COBOL program "${file.name}":\n` +
          `What business process does it support, who are the stakeholders,\n` +
          `what are the key inputs and outputs, and what is the business value of modernising it\n` +
          `from IDMS to a modern relational database with Java services?` +
          (idmsContext ? `\n\nIDMS Metadata:\n${idmsContext}` : '')
        result = await customPromptAI(code, prompt, file.name, file.name, ctx)

      } else if (action === 'assess-migration') {
        const prompt =
          `Assess the migration risk for converting this IDMS COBOL program "${file.name}" ` +
          `to modern Java with a relational database:\n` +
          `1. Data complexity: How complex are the IDMS record/set relationships to map to relational tables?\n` +
          `2. Code complexity: How many DML operations, paragraphs, and PERFORM calls need conversion?\n` +
          `3. Coupling: What IDMS-specific features (currency, sets, areas, run-units) create migration challenges?\n` +
          `4. Risk level: Rate as Low/Medium/High with justification.\n` +
          `5. Recommended migration approach: Big-bang vs incremental, estimated effort.` +
          (idmsContext ? `\n\nIDMS Metadata:\n${idmsContext}` : '')
        result = await customPromptAI(code, prompt, file.name, file.name, ctx)

      } else if (action === 'custom-prompt' && userPrompt) {
        const fullPrompt = userPrompt + (idmsContext ? `\n\nIDMS Context:\n${idmsContext}` : '')
        result = await customPromptAI(code, fullPrompt, file.name, file.name, ctx)
      }

      console.log('[IdmsAI] Action complete:', action, '| log_id:', result?.log_id)
      setAiResponse(result)
    } catch (err) {
      console.error('[IdmsAI] AI call failed:', action, err.message)
      setAiError(err.message || 'AI request failed')
    } finally {
      setAiLoading(false)
    }
  }

  const handleCustomPromptSubmit = (prompt) => runAI(prompt)

  // ── Save AI output to S3 project workspace ────────────────────────────────
  const handleSave = async (content) => {
    if (!content || !project?.account || !project?.name) return
    setSaving(true)
    setSaveStatus(null)

    const token = localStorage.getItem('modernizeit_token') || ''
    const user  = (() => { try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} } })()

    try {
      const resp = await fetch(`${API_BASE}/workspace/ai-outputs/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          account_id:       project.account,
          user_id:          user?.user_id || user?.id || '',
          application_name: project.name,
          action,
          source_filename:  file?.name || 'unknown',
          content,
          log_id:           aiResponse?.log_id || '',
        }),
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }

      const result = await resp.json()
      console.log('[IdmsAI] Saved output:', result.filename)
      setSaveStatus('saved')
    } catch (err) {
      console.error('[IdmsAI] Save failed:', err.message)
      setSaveStatus('error')
    } finally {
      setSaving(false)
    }
  }

  const handleRegenerate = () => {
    if (action === 'custom-prompt') {
      setAiResponse(null)
    } else {
      runAI()
    }
  }

  const modalResponse = aiError
    ? { explanation: `Error: ${aiError}`, response: `Error: ${aiError}` }
    : aiResponse

  return (
    <AIResponseModal
      isOpen={true}
      onClose={onClose}
      title={`${IDMS_ACTION_TITLE[action] || 'AI Analysis'} — ${file?.name}`}
      mode={IDMS_ACTION_MODE[action] || 'explain'}
      selectedCode={fileContent || idmsContext}
      sectionName={file?.name}
      aiResponse={modalResponse}
      aiLoading={aiLoading || contentLoading}
      onRegenerate={handleRegenerate}
      onAccept={null}
      onCustomPromptSubmit={handleCustomPromptSubmit}
      onSave={handleSave}
      saving={saving}
      saveStatus={saveStatus}
    />
  )
}


function InventoryTab({ programResults, dmlOps, copybooks, project, ingestJobId }) {
  const [selectedFile, setSelectedFile] = useState(null)
  const [searchText, setSearchText]     = useState('')
  const [activeScope, setActiveScope]   = useState('all')
  const [aiMenuFileId, setAiMenuFileId] = useState(null)
  const [aiModal, setAiModal]             = useState(null)   // { action, file }
  const [expandedGroups, setExpandedGroups] = useState(
    () => Object.fromEntries(IDMS_GROUP_ORDER.map(g => [g, true]))
  )

  // Build the flat file list (must be above early return — hooks can't be conditional)
  const allFiles = useMemo(
    () => buildIdmsInventory(programResults, copybooks, dmlOps),
    [programResults, copybooks, dmlOps]
  )

  // Close AI menu on outside click (must be above early return — hooks can't be conditional)
  useEffect(() => {
    if (!aiMenuFileId) return
    const handle = (e) => { if (!e.target.closest('[data-ai-actions]')) setAiMenuFileId(null) }
    document.addEventListener('click', handle)
    return () => document.removeEventListener('click', handle)
  }, [aiMenuFileId])

  if (!programResults) return <NotAvailable message="IDMS Analysis has not run yet." />

  // Compute KPIs
  const totalPrograms  = allFiles.filter(f => f.type !== 'copybook').length
  const totalIdms      = allFiles.filter(f => f.type === 'idms-program').length
  const totalCopybooks = allFiles.filter(f => f.type === 'copybook').length
  const totalDml       = allFiles.reduce((sum, f) => sum + (f.dmlCount || 0), 0)
  const totalLines     = allFiles.reduce((sum, f) => sum + (f.lines || 0), 0)

  // Filter by scope + search
  const filteredFiles = allFiles.filter(f => {
    // Scope filter
    if (activeScope === 'idms' && f.group !== 'IDMS PROGRAMS') return false
    if (activeScope === 'non-idms' && f.group !== 'NON-IDMS') return false
    if (activeScope === 'copybooks' && f.group !== 'COPYBOOKS') return false
    // Search filter
    if (searchText) {
      const term = searchText.toLowerCase()
      return (f.name || '').toLowerCase().includes(term) ||
             (f.subschema || '').toLowerCase().includes(term) ||
             (f.idmsMode || '').toLowerCase().includes(term)
    }
    return true
  })

  // Group the filtered files
  const grouped = IDMS_GROUP_ORDER.reduce((acc, g) => {
    const rows = filteredFiles.filter(f => f.group === g)
    if (rows.length) acc[g] = rows
    return acc
  }, {})

  const toggleGroup = (g) => setExpandedGroups(prev => ({ ...prev, [g]: !prev[g] }))

  return (
    <>
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left: Table area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* KPI Strip */}
        <div className="ga-metrics-strip" style={{ borderRadius: 0, borderBottom: '1px solid var(--border-color)' }}>
          {[
            { label: 'Programs',      value: totalPrograms },
            { label: 'IDMS Programs', value: totalIdms },
            { label: 'DML Ops',       value: totalDml },
            { label: 'Copybooks',     value: totalCopybooks },
            { label: 'Total Lines',   value: totalLines },
          ].map(m => (
            <div key={m.label} className="ga-metric-card">
              <span className="ga-metric-value">{m.value.toLocaleString()}</span>
              <span className="ga-metric-label">{m.label}</span>
            </div>
          ))}
        </div>

        {/* Search + scope chips */}
        <div style={{
          padding: '8px 16px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <Search size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            type="text" placeholder="Search programs, copybooks..."
            value={searchText} onChange={e => setSearchText(e.target.value)}
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: 'var(--text-primary)', fontSize: 13,
            }}
          />
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {IDMS_SCOPE_CHIPS.map(chip => (
              <button
                key={chip.key}
                onClick={() => setActiveScope(chip.key)}
                style={{
                  padding: '3px 10px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  fontSize: 11, fontWeight: activeScope === chip.key ? 600 : 400,
                  background: activeScope === chip.key ? 'var(--bg-tertiary)' : 'transparent',
                  color: activeScope === chip.key ? 'var(--text-primary)' : 'var(--text-muted)',
                }}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
            {filteredFiles.length} files
          </span>
        </div>

        {/* Grouped table */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {Object.keys(grouped).length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No files match your filter.
            </div>
          ) : (
            IDMS_GROUP_ORDER.map(group => {
              const rows = grouped[group]
              if (!rows) return null
              const isExpanded = expandedGroups[group] !== false
              const groupColor = IDMS_GROUP_COLORS[group] || '#6b7280'

              return (
                <div key={group}>
                  {/* Group header */}
                  <button
                    onClick={() => toggleGroup(group)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '8px 16px', border: 'none', cursor: 'pointer',
                      background: 'var(--bg-secondary)',
                      borderBottom: '1px solid var(--border-color)',
                      fontSize: 12, fontWeight: 600, color: groupColor, textAlign: 'left',
                    }}
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: groupColor, flexShrink: 0,
                    }} />
                    {group}
                    <span style={{
                      marginLeft: 'auto', padding: '1px 8px', borderRadius: 10,
                      fontSize: 10, fontWeight: 600,
                      background: `${groupColor}18`, color: groupColor,
                    }}>
                      {rows.length}
                    </span>
                  </button>

                  {/* Group rows */}
                  {isExpanded && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-primary)' }}>
                          {['Name', 'DML Ops', 'Lines', 'Subschema', 'Status', 'AI Actions'].map(h => (
                            <th key={h} style={{
                              padding: '5px 12px', textAlign: 'left', fontWeight: 600,
                              color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)',
                              fontSize: 10, textTransform: 'uppercase',
                              ...(h === 'AI Actions' ? { width: 80, textAlign: 'center' } : {}),
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(file => (
                          <tr
                            key={file.id}
                            onClick={(e) => { if (!e.target.closest('[data-ai-actions]')) setSelectedFile(file) }}
                            style={{
                              cursor: 'pointer',
                              backgroundColor: selectedFile?.id === file.id ? 'var(--bg-tertiary)' : 'transparent',
                            }}
                            onMouseEnter={e => { if (selectedFile?.id !== file.id) e.currentTarget.style.backgroundColor = 'var(--bg-secondary)' }}
                            onMouseLeave={e => { if (selectedFile?.id !== file.id) e.currentTarget.style.backgroundColor = 'transparent' }}
                          >
                            <td style={{
                              padding: '7px 12px', borderBottom: '1px solid var(--border-color)',
                              color: 'var(--text-primary)', fontFamily: 'monospace', fontWeight: 500,
                            }}>
                              {file.name}
                            </td>
                            <td style={{
                              padding: '7px 12px', borderBottom: '1px solid var(--border-color)',
                              color: file.dmlCount > 0 ? '#388bfd' : 'var(--text-muted)',
                              fontWeight: file.dmlCount > 0 ? 600 : 400,
                            }}>
                              {file.type === 'copybook' ? (file.fieldCount != null ? `${file.fieldCount}f` : '—') : file.dmlCount}
                            </td>
                            <td style={{
                              padding: '7px 12px', borderBottom: '1px solid var(--border-color)',
                              color: 'var(--text-muted)', fontFamily: 'monospace',
                            }}>
                              {file.lines > 0 ? file.lines.toLocaleString() : '—'}
                            </td>
                            <td style={{
                              padding: '7px 12px', borderBottom: '1px solid var(--border-color)',
                              color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: 11,
                            }}>
                              {file.subschema || (file.type === 'copybook' && file.verified ? 'Verified' : '—')}
                            </td>
                            <td style={{
                              padding: '7px 12px', borderBottom: '1px solid var(--border-color)',
                            }}>
                              <span style={{
                                padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                                color: file.type === 'idms-program' ? '#10b981' : '#6b7280',
                                background: file.type === 'idms-program' ? '#10b98118' : '#6b728018',
                              }}>
                                {file.type === 'idms-program' ? 'Classified' : file.type === 'copybook' ? 'Classified' : 'Classified'}
                              </span>
                            </td>
                            <td data-ai-actions style={{
                              padding: '7px 12px', borderBottom: '1px solid var(--border-color)',
                              textAlign: 'center', position: 'relative',
                            }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); setAiMenuFileId(aiMenuFileId === file.id ? null : file.id) }}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  color: 'var(--text-muted)', padding: '2px 6px', borderRadius: 4,
                                }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                              >
                                <MoreVertical size={14} />
                              </button>
                              {aiMenuFileId === file.id && (
                                <div
                                  style={{
                                    position: 'absolute', right: 12, top: '100%', zIndex: 100,
                                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                                    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                                    minWidth: 200, overflow: 'hidden',
                                  }}
                                >
                                  {/* Menu header */}
                                  <div style={{
                                    padding: '8px 12px', borderBottom: '1px solid var(--border-color)',
                                    display: 'flex', alignItems: 'center', gap: 6,
                                  }}>
                                    <FileBarChart size={12} style={{ color: '#388bfd' }} />
                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                                      {file.name}
                                    </span>
                                  </div>
                                  {IDMS_AI_ACTIONS.map(act => {
                                    const Icon = act.icon
                                    return (
                                      <button
                                        key={act.id}
                                        onClick={(e) => { e.stopPropagation(); setAiMenuFileId(null); setAiModal({ action: act.id, file }) }}
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: 8,
                                          width: '100%', padding: '8px 12px', border: 'none',
                                          background: 'transparent', cursor: 'pointer',
                                          color: 'var(--text-secondary)', fontSize: 12, textAlign: 'left',
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = 'var(--text-primary)' }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' }}
                                      >
                                        <Icon size={14} style={{ flexShrink: 0 }} />
                                        {act.label}
                                      </button>
                                    )
                                  })}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Right: Detail panel */}
      <div style={{
        width: 340, borderLeft: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)', flexShrink: 0,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <IdmsDetailPanel file={selectedFile} onClose={() => setSelectedFile(null)} ingestJobId={ingestJobId} />
      </div>
    </div>
    {aiModal && (
      <IdmsInventoryAIModal
        action={aiModal.action}
        file={aiModal.file}
        ingestJobId={ingestJobId}
        project={project}
        onClose={() => setAiModal(null)}
      />
    )}
    </>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: DML Operations — Migration-focused dashboard
// ═══════════════════════════════════════════════════════════════════════════════

const MIGRATION_DIFFICULTY_ORDER = ['easy', 'moderate', 'lifecycle', 'hard']

const MIGRATION_TARGETS = [
  { key: 'jdbc', label: 'SQL / JDBC' },
  { key: 'db2',  label: 'DB2' },
]

const MIGRATION_META = {
  jdbc: {
    easy:      { label: 'Direct SQL Mapping',       desc: 'Maps directly to SQL SELECT, INSERT, UPDATE, or DELETE via JDBC PreparedStatement.' },
    moderate:  { label: 'Cursor / JOIN Rewrite',    desc: 'Set navigation requires JDBC ResultSet cursor iteration or SQL JOIN rewrites.' },
    lifecycle: { label: 'Connection / Transaction', desc: 'Maps to JDBC connection open, transaction begin/commit/close.' },
    hard:      { label: 'Manual Rewrite',           desc: 'No SQL equivalent — requires manual Java/JDBC implementation.' },
  },
  db2: {
    easy:      { label: 'Direct DB2 SQL',           desc: 'Maps directly to DB2 SQL SELECT, INSERT, UPDATE, or DELETE with host variables.' },
    moderate:  { label: 'DB2 Cursor / JOIN',        desc: 'Set navigation maps to DB2 DECLARE CURSOR / FETCH or multi-table JOINs.' },
    lifecycle: { label: 'DB2 Connect / Commit',     desc: 'Maps to DB2 CONNECT, COMMIT WORK, ROLLBACK WORK.' },
    hard:      { label: 'Stored Procedure / Logic', desc: 'No direct DB2 SQL equivalent — requires stored procedure or application logic rewrite.' },
  },
}

const MIGRATION_COLORS = {
  easy:      { color: '#3fb950', bg: 'rgba(63,185,80,0.08)' },
  moderate:  { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
  lifecycle: { color: '#3b82f6', bg: 'rgba(59,130,246,0.08)' },
  hard:      { color: '#ef4444', bg: 'rgba(239,68,68,0.08)' },
}

// DML verb reference — SQL equivalent, Java pattern, and plain-English description
// Used as fallback when MongoDB registry data isn't available
const DML_VERB_REFERENCE = {
  OBTAIN_CALC:       { sql: 'SELECT * FROM {record} WHERE {calc_key} = :value', java: '{Record} r = {record}Repository.findBy{CalcKey}(value);', desc: 'Retrieve a record by its CALC key (hashed lookup). Equivalent to a SQL SELECT with a WHERE clause on the primary key.' },
  OBTAIN_BY_DBKEY:   { sql: 'SELECT * FROM {record} WHERE db_key = :dbkey', java: '{Record} r = {record}Repository.findById(dbKey);', desc: 'Retrieve a record by its database key (direct address). Like a SQL SELECT by primary key / row ID.' },
  OBTAIN_FIRST:      { sql: 'SELECT * FROM {record} WHERE {set_key} = :owner ORDER BY sort_key ASC LIMIT 1', java: '{Record} r = {record}Repository.findFirstBy{Set}(ownerKey);', desc: 'Retrieve the first member record in a set. Maps to a SQL SELECT with ORDER BY and LIMIT 1.' },
  OBTAIN_NEXT:       { sql: '-- FETCH NEXT from cursor on {record}', java: 'iterator.next(); // cursor-based iteration over {record}', desc: 'Retrieve the next record in the current set traversal. Maps to JDBC ResultSet cursor FETCH NEXT.' },
  OBTAIN_PRIOR:      { sql: '-- FETCH PRIOR (scrollable cursor) on {record}', java: 'iterator.previous(); // scrollable cursor on {record}', desc: 'Retrieve the previous record in set order. Requires a scrollable JDBC ResultSet or reverse ORDER BY.' },
  OBTAIN_LAST:       { sql: 'SELECT * FROM {record} WHERE {set_key} = :owner ORDER BY sort_key DESC LIMIT 1', java: '{Record} r = {record}Repository.findLastBy{Set}(ownerKey);', desc: 'Retrieve the last member record in a set. Maps to SQL SELECT with DESC ORDER BY and LIMIT 1.' },
  OBTAIN_OWNER:      { sql: 'SELECT owner.* FROM {owner_record} owner JOIN {set} s ON owner.db_key = s.owner_db_key WHERE s.member_db_key = :currentDbKey', java: 'Optional<{OwnerRecord}> owner = {ownerRecord}Repository.findOwnerOf{Set}(currentRecord);', desc: 'Navigate from a member record to its owner record in a set relationship. Maps to a SQL JOIN between child and parent tables.' },
  OBTAIN_CURRENT:    { sql: '-- Re-read current record (already in buffer)', java: '// Record already loaded — re-read from current context', desc: 'Re-obtain the current record (already positioned). Typically a no-op in SQL — the data is already in the application buffer.' },
  OBTAIN_WITHIN_SET: { sql: 'SELECT * FROM {record} WHERE {set_key} = :owner ORDER BY sort_key', java: 'List<{Record}> members = {record}Repository.findBy{Set}(ownerKey);', desc: 'Retrieve records within a set (by set membership). Maps to a SQL SELECT with a foreign key JOIN.' },
  FIND_CALC:         { sql: 'SELECT db_key FROM {record} WHERE {calc_key} = :value', java: 'Long dbKey = {record}Repository.findDbKeyBy{CalcKey}(value);', desc: 'Locate a record by CALC key without retrieving data. Like a SQL SELECT that only returns the row pointer.' },
  FIND_BY_DBKEY:     { sql: '-- Position cursor to db_key (no data transfer)', java: '// Set cursor position to dbKey', desc: 'Position to a record by database key without data transfer. Sets currency only — no equivalent SQL read needed.' },
  FIND_FIRST:        { sql: '-- DECLARE CURSOR ... ORDER BY ASC; OPEN; (no FETCH yet)', java: 'cursor.moveToFirst(); // position without fetch', desc: 'Position to the first member in a set without retrieving data. Establishes cursor position for subsequent OBTAIN NEXT.' },
  FIND_NEXT:         { sql: '-- Advance cursor position (no FETCH)', java: 'cursor.moveToNext(); // advance position', desc: 'Advance to the next set member without data transfer. Moves the cursor but does not retrieve the record.' },
  FIND_PRIOR:        { sql: '-- Scroll cursor backward (no FETCH)', java: 'cursor.moveToPrevious(); // reverse cursor', desc: 'Move to the previous set member without data transfer. Requires scrollable cursor support.' },
  FIND_LAST:         { sql: '-- Position cursor to last row', java: 'cursor.moveToLast();', desc: 'Position to the last member in a set without retrieving data.' },
  FIND_OWNER:        { sql: '-- Locate owner record (no data transfer)', java: '// Navigate to owner — position only', desc: 'Locate the owner record of the current member without retrieving data. Establishes owner currency.' },
  FIND_CURRENT:      { sql: '-- Already positioned (no-op)', java: '// Already positioned', desc: 'Confirm current record position. Typically a no-op in relational systems.' },
  FIND_WITHIN_SET:   { sql: '-- DECLARE CURSOR for set members', java: 'cursor = {record}Repository.openCursorBy{Set}(ownerKey);', desc: 'Position to a record within a set by set membership. Establishes set traversal cursor.' },
  STORE:             { sql: 'INSERT INTO {record} ({columns}) VALUES ({values})', java: '{record}Repository.save(new {Record}({fields}));', desc: 'Store a new record in the database. Direct equivalent to SQL INSERT.' },
  MODIFY:            { sql: 'UPDATE {record} SET {columns} = {values} WHERE db_key = :currentDbKey', java: '{record}Repository.save(existingRecord);', desc: 'Modify the current record. Direct equivalent to SQL UPDATE with a WHERE clause on the current row.' },
  ERASE:             { sql: 'DELETE FROM {record} WHERE db_key = :currentDbKey', java: '{record}Repository.delete(currentRecord);', desc: 'Delete the current record. Direct equivalent to SQL DELETE. ERASE ALL also removes member records (CASCADE).' },
  CONNECT:           { sql: 'UPDATE {member_record} SET {set_fk} = :ownerKey WHERE db_key = :currentDbKey', java: 'memberRecord.set{Set}Owner(ownerRecord); {member}Repository.save(memberRecord);', desc: 'Connect a record to a set (establish relationship). Maps to setting a foreign key in the member table.' },
  DISCONNECT:        { sql: 'UPDATE {member_record} SET {set_fk} = NULL WHERE db_key = :currentDbKey', java: 'memberRecord.set{Set}Owner(null); {member}Repository.save(memberRecord);', desc: 'Disconnect a record from a set (remove relationship). Maps to NULLing the foreign key in the member table.' },
  BIND_RUN_UNIT:     { sql: '-- Connection.open() / DataSource.getConnection()', java: 'Connection conn = dataSource.getConnection();', desc: 'Initialize the IDMS run unit (session). Equivalent to opening a JDBC database connection.' },
  BIND:              { sql: '-- Bind record/subschema for access', java: '// Map entity class for access', desc: 'Bind a record or subschema for access within the run unit. Equivalent to entity registration / schema binding.' },
  READY:             { sql: '-- Begin area access (READ / UPDATE mode)', java: '// entityManager.setFlushMode() or begin access scope', desc: 'Ready an area for access in the specified mode (RETRIEVAL or UPDATE). Maps to setting transaction isolation or access mode.' },
  FINISH:            { sql: 'COMMIT; Connection.close();', java: 'conn.commit(); conn.close();', desc: 'End the run unit — commit changes and release resources. Maps to JDBC commit + connection close.' },
  FINISH_TASK:       { sql: '-- End task-level unit of work', java: '// Task cleanup + connection release', desc: 'End a task-level run unit. Commits and releases task-scoped resources.' },
  COMMIT:            { sql: 'COMMIT', java: 'conn.commit();', desc: 'Commit the current transaction. Direct equivalent to SQL COMMIT.' },
  COMMIT_ALL:        { sql: 'COMMIT', java: 'transactionManager.commit();', desc: 'Commit all pending changes across all areas. Same as SQL COMMIT but emphasizes all-area scope.' },
  ROLLBACK:          { sql: 'ROLLBACK', java: 'conn.rollback();', desc: 'Roll back the current transaction. Direct equivalent to SQL ROLLBACK.' },
  ROLLBACK_ALL:      { sql: 'ROLLBACK', java: 'transactionManager.rollback();', desc: 'Roll back all changes across all areas.' },
  RETURN:            { sql: '-- No SQL equivalent (IDMS DC control transfer)', java: '// return; // DC program control flow', desc: 'Return control to the IDMS DC system (online transaction). No SQL equivalent — this is a program control flow command.' },
  ACCEPT:            { sql: '-- No SQL equivalent (IDMS currency/status query)', java: '// Read IDMS status: dbStatus, currentDbKey, etc.', desc: 'Accept system information — current database key, error status, statistics. No SQL equivalent — maps to checking JDBC metadata or connection state.' },
  GET:               { sql: '-- Transfer data from current record buffer', java: '// Record already fetched — map fields to variables', desc: 'Transfer data from the current record buffer to working storage. In JDBC, data is already available after a SELECT — this is implicit.' },
  IF_MEMBER:         { sql: 'SELECT COUNT(*) FROM {set} WHERE member_db_key = :currentDbKey', java: 'boolean isMember = {set}Repository.existsByMember(currentRecord);', desc: 'Test whether the current record is a member of a specified set. Maps to a SQL EXISTS or COUNT query on the relationship table.' },
  IF_EMPTY:          { sql: 'SELECT COUNT(*) FROM {member_record} WHERE {set_fk} = :ownerKey', java: 'boolean isEmpty = {member}Repository.countBy{Set}(ownerKey) == 0;', desc: 'Test whether a set owned by the current record has no members. Maps to a SQL COUNT query checking for zero members.' },
  IF_NOT_EMPTY:      { sql: 'SELECT COUNT(*) FROM {member_record} WHERE {set_fk} = :ownerKey', java: 'boolean hasMembers = {member}Repository.countBy{Set}(ownerKey) > 0;', desc: 'Test whether a set owned by the current record has at least one member.' },
  ACCEPT_CURRENCY:   { sql: '-- Read current cursor position / db_key', java: 'Long currentDbKey = getCurrentDbKey();', desc: 'Accept the current database key (currency). No SQL equivalent — maps to reading the current cursor position or entity ID.' },
}

// Get SQL/Java/description for a DML operation — from data or fallback
function getDmlReference(op) {
  const verb = (op.verb_type || op.verb || '').toUpperCase().replace(/\s+/g, '_')
  const ref = DML_VERB_REFERENCE[verb] || DML_VERB_REFERENCE[op.verb?.toUpperCase()] || null
  const sql = op.sql_pattern || (ref?.sql || '')
  const java = op.java_pattern || (ref?.java || '')
  const desc = ref?.desc || ''

  // Substitute record/set names into templates
  const rec = op.record_name || '{record}'
  const set = op.set_name || '{set}'
  const subs = (s) => s.replace(/\{record\}/gi, rec).replace(/\{Record\}/g, rec).replace(/\{set\}/gi, set).replace(/\{Set\}/g, set)
    .replace(/\{owner_record\}/gi, 'owner').replace(/\{OwnerRecord\}/g, 'Owner')
    .replace(/\{member_record\}/gi, rec).replace(/\{member\}/gi, rec)
    .replace(/\{calc_key\}/gi, 'calc_key').replace(/\{CalcKey\}/g, 'CalcKey')
    .replace(/\{set_key\}/gi, `${set}_fk`).replace(/\{set_fk\}/gi, `${set}_fk`)
    .replace(/\{columns\}/g, '...').replace(/\{values\}/g, '...').replace(/\{fields\}/g, '...')

  return { sql: subs(sql), java: subs(java), desc: subs(desc) }
}

function getMigrationDifficulty(op) {
  const verb = (op.verb || '').toUpperCase()
  const cat  = op.category || 'other'
  if (cat === 'lifecycle') return 'lifecycle'
  if (cat === 'currency' || cat === 'error_handling') return 'hard'
  if (verb.includes('NEXT') || verb.includes('FIRST') || verb.includes('LAST') ||
      verb.includes('PRIOR') || verb.includes('WITHIN') || verb.includes('OWNER')) return 'moderate'
  if (verb === 'RETURN' || verb.includes('QUEUE') || verb.includes('IF ')) return 'hard'
  if (cat === 'retrieval' || cat === 'modification') return 'easy'
  if (cat === 'navigation') return 'moderate'
  return 'moderate'
}

function DmlTab({ dmlOps, copybooks, onNavigateToCopybook, programResults }) {
  const [selectedOp, setSelectedOp]           = useState(null)
  const [tableFilter, setTableFilter]         = useState('')   // verb, record, or program to pre-filter the table
  const [searchTerm, setSearchTerm]           = useState('')
  const [dmlSubTab, setDmlSubTab]             = useState('migration')  // 'migration' | 'operations'
  const [migrationTarget, setMigrationTarget] = useState('jdbc')
  const [selectedProgram, setSelectedProgram] = useState(null) // selected program file in Operations view

  if (!dmlOps) return <NotAvailable message="IDMS Analysis has not run yet." />

  const allOps = Array.isArray(dmlOps) ? dmlOps : (dmlOps?.operations || [])

  // Build set of known copybook names for cross-linking
  const cbNames = useMemo(() => {
    const cbs = Array.isArray(copybooks) ? copybooks : (copybooks?.copybooks || [])
    return new Set(cbs.map(cb => (cb.name || '').toUpperCase()))
  }, [copybooks])

  // Build program file list for left sidebar (from programResults, more reliable than op.program)
  const programFiles = useMemo(() => {
    if (!programResults || typeof programResults !== 'object') return []
    return Object.entries(programResults)
      .filter(([, prog]) => prog.idms && prog.idms.operation_count > 0)
      .map(([stem, prog]) => ({
        name: prog.filename || stem,
        stem,
        dmlCount: prog.idms.operation_count || 0,
        subschema: prog.idms.subschema || '',
        operations: (prog.idms.operations || []).map(op => ({ ...op, program: prog.filename || stem })),
      }))
      .sort((a, b) => b.dmlCount - a.dmlCount)
  }, [programResults])

  const totalOps       = allOps.length
  const uniquePrograms = programFiles.length || new Set(allOps.map(o => o.program).filter(Boolean)).size
  const uniqueRecords  = new Set(allOps.map(o => o.record_name).filter(Boolean)).size
  const uniqueSets     = new Set(allOps.map(o => o.set_name).filter(Boolean)).size

  // ── 1. Migration difficulty classification ──
  const diffGroups = { easy: [], moderate: [], lifecycle: [], hard: [] }
  for (const op of allOps) diffGroups[getMigrationDifficulty(op)].push(op)

  // Per-difficulty verb breakdown
  const diffVerbs = {}
  for (const [diff, ops] of Object.entries(diffGroups)) {
    const vc = {}
    for (const op of ops) {
      const v = op.verb || 'UNKNOWN'
      if (!vc[v]) vc[v] = { count: 0, sql: op.sql_pattern || null }
      vc[v].count++
    }
    diffVerbs[diff] = Object.entries(vc).sort((a, b) => b[1].count - a[1].count)
  }

  // ── 2. Record access profile ──
  const recordMap = {}
  for (const op of allOps) {
    const rec = op.record_name
    if (!rec) continue
    if (!recordMap[rec]) recordMap[rec] = { retrieval: 0, navigation: 0, modification: 0, lifecycle: 0, other: 0, total: 0 }
    const cat = op.category || 'other'
    recordMap[rec][cat] !== undefined ? recordMap[rec][cat]++ : recordMap[rec].other++
    recordMap[rec].total++
  }
  const topRecords = Object.entries(recordMap).sort((a, b) => b[1].total - a[1].total).slice(0, 10)
  const maxRecTotal = topRecords.length ? topRecords[0][1].total : 1

  // ── 3. Program breakdown ──
  const progMap = {}
  for (const op of allOps) {
    const p = op.program || 'Unknown'
    if (!progMap[p]) progMap[p] = { total: 0, easy: 0, moderate: 0, lifecycle: 0, hard: 0 }
    progMap[p].total++
    progMap[p][getMigrationDifficulty(op)]++
  }
  const progList = Object.entries(progMap).sort((a, b) => b[1].total - a[1].total)
  const maxProgTotal = progList.length ? progList[0][1].total : 1

  // ── Table filter (ops shown in drill-down table) ──
  // When a program is selected, use its operations; otherwise use allOps
  const baseOps = selectedProgram
    ? (programFiles.find(p => p.stem === selectedProgram)?.operations || [])
    : allOps
  const tableOps = baseOps.filter(op => {
    if (tableFilter) {
      const tf = tableFilter.toLowerCase()
      if (!(op.verb || '').toLowerCase().includes(tf) &&
          !(op.record_name || '').toLowerCase().includes(tf) &&
          !(op.program || '').toLowerCase().includes(tf)) return false
    }
    if (searchTerm) {
      const st = searchTerm.toLowerCase()
      return (op.verb || '').toLowerCase().includes(st) ||
             (op.record_name || '').toLowerCase().includes(st) ||
             (op.program || '').toLowerCase().includes(st)
    }
    return true
  })

  const handleDrillDown = (filterValue) => {
    setTableFilter(filterValue)
    setDmlSubTab('operations')
    setSelectedOp(null)
  }

  // ── Percentage helper ──
  const pct = (n) => totalOps > 0 ? Math.round((n / totalOps) * 100) : 0

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left: Dashboard */}
      <div style={{ flex: 1, overflowY: 'auto' }}>

        {/* KPI Strip */}
        <div className="ga-metrics-strip" style={{ borderRadius: 0, borderBottom: '1px solid var(--border-color)' }}>
          {[
            { label: 'DML Operations', value: totalOps },
            { label: 'Programs',       value: uniquePrograms },
            { label: 'Records',        value: uniqueRecords },
            { label: 'Sets',           value: uniqueSets },
          ].map(m => (
            <div key={m.label} className="ga-metric-card">
              <span className="ga-metric-value">{m.value.toLocaleString()}</span>
              <span className="ga-metric-label">{m.label}</span>
            </div>
          ))}
        </div>

        {/* Sub-tab bar */}
        <div style={{
          display: 'flex', gap: 0, padding: '0 16px',
          borderBottom: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)',
        }}>
          {[
            { key: 'migration', label: 'Migration Complexity' },
            { key: 'operations', label: `Operations (${tableOps.length})` },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setDmlSubTab(t.key)}
              style={{
                padding: '8px 16px', border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: dmlSubTab === t.key ? 600 : 400,
                color: dmlSubTab === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
                background: 'transparent',
                borderBottom: dmlSubTab === t.key ? '2px solid var(--accent-color, #3b82f6)' : '2px solid transparent',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Sub-tab: Migration Complexity ── */}
        {dmlSubTab === 'migration' && <>

        {/* ── Section 1: Migration Complexity ── */}
        <div style={{ padding: '20px 20px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Migration Complexity
            </div>
            {/* Target toggle: SQL/JDBC vs DB2 */}
            <div style={{
              display: 'flex', borderRadius: 6, overflow: 'hidden',
              border: '1px solid var(--border-color)',
            }}>
              {MIGRATION_TARGETS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setMigrationTarget(t.key)}
                  style={{
                    padding: '4px 14px', border: 'none', cursor: 'pointer',
                    fontSize: 11, fontWeight: migrationTarget === t.key ? 600 : 400,
                    background: migrationTarget === t.key ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                    color: migrationTarget === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            How each DML operation maps to {migrationTarget === 'db2' ? 'DB2 SQL' : 'SQL/JDBC'} — grouped by migration difficulty.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {MIGRATION_DIFFICULTY_ORDER.map(diff => {
              const colors = MIGRATION_COLORS[diff]
              const meta   = MIGRATION_META[migrationTarget][diff]
              const ops    = diffGroups[diff]
              const verbs  = diffVerbs[diff] || []
              if (!ops.length) return null
              return (
                <div key={diff} style={{
                  borderRadius: 8, border: `1px solid ${colors.color}30`,
                  background: colors.bg, padding: 14,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: colors.color }}>{meta.label}</span>
                    <span style={{ fontSize: 22, fontWeight: 700, color: colors.color }}>{ops.length}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                    {pct(ops.length)}% of all operations
                  </div>
                  <div style={{ fontSize: 11, color: colors.color, marginBottom: 8, lineHeight: 1.4, opacity: 0.8 }}>
                    {meta.desc}
                  </div>
                  {verbs.slice(0, 6).map(([verb, { count, sql }]) => (
                    <div
                      key={verb}
                      onClick={() => handleDrillDown(verb)}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '3px 0', cursor: 'pointer', fontSize: 11,
                      }}
                    >
                      <span style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>{verb}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {sql && (
                          <span style={{ fontSize: 9, color: '#3b82f6', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sql.split('\n')[0].substring(0, 20)}
                          </span>
                        )}
                        <span style={{ color: 'var(--text-muted)', fontWeight: 600, minWidth: 20, textAlign: 'right' }}>{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Section 2: Record Access Profile ── */}
        {topRecords.length > 0 && (
          <div style={{ padding: '24px 20px 0' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
              Record Access Profile
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
              Most-accessed IDMS records — shows read vs. navigation vs. write patterns.
            </div>
            {topRecords.map(([rec, counts]) => (
              <div
                key={rec}
                onClick={() => handleDrillDown(rec)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, cursor: 'pointer' }}
              >
                <span style={{
                  width: 150, fontSize: 11, fontFamily: 'monospace',
                  color: 'var(--text-primary)', flexShrink: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {rec}
                </span>
                <div style={{ flex: 1, display: 'flex', height: 16, borderRadius: 3, overflow: 'hidden', background: 'var(--bg-primary)' }}>
                  {counts.retrieval > 0 && (
                    <div title={`Retrieval: ${counts.retrieval}`} style={{
                      width: `${(counts.retrieval / maxRecTotal) * 100}%`,
                      background: '#10b981', opacity: 0.7,
                    }} />
                  )}
                  {counts.navigation > 0 && (
                    <div title={`Navigation: ${counts.navigation}`} style={{
                      width: `${(counts.navigation / maxRecTotal) * 100}%`,
                      background: '#8b5cf6', opacity: 0.7,
                    }} />
                  )}
                  {counts.modification > 0 && (
                    <div title={`Modification: ${counts.modification}`} style={{
                      width: `${(counts.modification / maxRecTotal) * 100}%`,
                      background: '#f59e0b', opacity: 0.7,
                    }} />
                  )}
                  {(counts.lifecycle + counts.other) > 0 && (
                    <div title={`Other: ${counts.lifecycle + counts.other}`} style={{
                      width: `${((counts.lifecycle + counts.other) / maxRecTotal) * 100}%`,
                      background: '#6b7280', opacity: 0.5,
                    }} />
                  )}
                </div>
                <span style={{ width: 28, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>
                  {counts.total}
                </span>
              </div>
            ))}
            {/* Legend */}
            <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 10, color: 'var(--text-muted)' }}>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#10b981', marginRight: 4 }} />Retrieval</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#8b5cf6', marginRight: 4 }} />Navigation</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#f59e0b', marginRight: 4 }} />Modification</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#6b7280', marginRight: 4 }} />Other</span>
            </div>
          </div>
        )}

        {/* ── Section 3: Program Breakdown ── */}
        {progList.length > 0 && (
          <div style={{ padding: '24px 20px 0' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
              Program DML Profile
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
              DML operations per program — colored by migration difficulty.
            </div>
            {progList.map(([prog, counts]) => (
              <div
                key={prog}
                onClick={() => handleDrillDown(prog)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, cursor: 'pointer' }}
              >
                <span style={{
                  width: 180, fontSize: 11, fontFamily: 'monospace',
                  color: 'var(--text-primary)', flexShrink: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {prog}
                </span>
                <div style={{ flex: 1, display: 'flex', height: 16, borderRadius: 3, overflow: 'hidden', background: 'var(--bg-primary)' }}>
                  {counts.easy > 0 && (
                    <div title={`${MIGRATION_META[migrationTarget].easy.label}: ${counts.easy}`} style={{
                      width: `${(counts.easy / maxProgTotal) * 100}%`,
                      background: MIGRATION_COLORS.easy.color, opacity: 0.7,
                    }} />
                  )}
                  {counts.moderate > 0 && (
                    <div title={`${MIGRATION_META[migrationTarget].moderate.label}: ${counts.moderate}`} style={{
                      width: `${(counts.moderate / maxProgTotal) * 100}%`,
                      background: MIGRATION_COLORS.moderate.color, opacity: 0.7,
                    }} />
                  )}
                  {counts.lifecycle > 0 && (
                    <div title={`${MIGRATION_META[migrationTarget].lifecycle.label}: ${counts.lifecycle}`} style={{
                      width: `${(counts.lifecycle / maxProgTotal) * 100}%`,
                      background: MIGRATION_COLORS.lifecycle.color, opacity: 0.7,
                    }} />
                  )}
                  {counts.hard > 0 && (
                    <div title={`${MIGRATION_META[migrationTarget].hard.label}: ${counts.hard}`} style={{
                      width: `${(counts.hard / maxProgTotal) * 100}%`,
                      background: MIGRATION_COLORS.hard.color, opacity: 0.7,
                    }} />
                  )}
                </div>
                <span style={{ width: 28, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>
                  {counts.total}
                </span>
              </div>
            ))}
            {/* Legend */}
            <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 10, color: 'var(--text-muted)' }}>
              {MIGRATION_DIFFICULTY_ORDER.map(d => (
                <span key={d}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: MIGRATION_COLORS[d].color, marginRight: 4 }} />
                  {MIGRATION_META[migrationTarget][d].label}
                </span>
              ))}
            </div>
          </div>
        )}

        </>}

        {/* ── Sub-tab: Operations ── */}
        {dmlSubTab === 'operations' && (
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

            {/* Left: Program file list */}
            {programFiles.length > 0 && (
              <div style={{
                width: 220, borderRight: '1px solid var(--border-color)',
                display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden',
              }}>
                {/* "All Programs" option */}
                <div
                  onClick={() => { setSelectedProgram(null); setSelectedOp(null) }}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    borderBottom: '1px solid var(--border-color)',
                    background: !selectedProgram ? 'var(--bg-tertiary)' : 'transparent',
                    color: !selectedProgram ? '#3b82f6' : 'var(--text-secondary)',
                  }}
                >
                  All Programs
                  <span style={{ float: 'right', fontSize: 10, color: 'var(--text-muted)', fontWeight: 400 }}>{totalOps} ops</span>
                </div>

                {/* Program list */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {programFiles.map(pf => (
                    <div
                      key={pf.stem}
                      onClick={() => { setSelectedProgram(pf.stem); setSelectedOp(null); setSearchTerm('') }}
                      style={{
                        padding: '6px 12px', cursor: 'pointer', fontSize: 11,
                        borderBottom: '1px solid var(--border-color)',
                        background: selectedProgram === pf.stem ? 'var(--bg-tertiary)' : 'transparent',
                        color: selectedProgram === pf.stem ? 'var(--text-primary)' : 'var(--text-secondary)',
                      }}
                      onMouseEnter={e => { if (selectedProgram !== pf.stem) e.currentTarget.style.background = 'var(--bg-primary)' }}
                      onMouseLeave={e => { if (selectedProgram !== pf.stem) e.currentTarget.style.background = 'transparent' }}
                    >
                      <div style={{ fontFamily: 'monospace', fontWeight: selectedProgram === pf.stem ? 600 : 400, marginBottom: 2 }}>
                        {pf.name}
                      </div>
                      <div style={{ display: 'flex', gap: 8, fontSize: 10, color: 'var(--text-muted)' }}>
                        <span style={{ color: '#3b82f6', fontWeight: 600 }}>{pf.dmlCount} ops</span>
                        {pf.subschema && <span>{pf.subschema}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Right: Operations table */}
            <div style={{ flex: 1, padding: '12px 16px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Filter indicator */}
              {tableFilter && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                  Filtered to: <span style={{ fontFamily: 'monospace', color: '#388bfd' }}>{tableFilter}</span>
                  <button
                    onClick={() => { setTableFilter(''); setSearchTerm('') }}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 11, marginLeft: 6 }}
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Program header when selected */}
              {selectedProgram && (() => {
                const pf = programFiles.find(p => p.stem === selectedProgram)
                return pf ? (
                  <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{pf.name}</span>
                    <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>{pf.dmlCount} DML ops</span>
                    {pf.subschema && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }}>{pf.subschema}</span>}
                  </div>
                ) : null
              })()}

              {/* Search bar */}
              <div style={{
                display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8,
                padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)', flexShrink: 0,
              }}>
                <Search size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <input
                  type="text" placeholder="Search operations..."
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  style={{
                    flex: 1, background: 'none', border: 'none', outline: 'none',
                    color: 'var(--text-primary)', fontSize: 12,
                  }}
                />
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{tableOps.length} ops</span>
              </div>

              {/* Table — fills remaining space */}
              <div style={{
                flex: 1, overflowY: 'auto', borderRadius: 8,
                border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: 'var(--bg-secondary)', zIndex: 1 }}>
                      {(selectedProgram ? ['Line', 'Verb', 'Category', 'Record', 'Set/Area'] : ['Line', 'Program', 'Verb', 'Category', 'Record', 'Set/Area']).map(h => (
                        <th key={h} style={{
                          padding: '6px 10px', textAlign: 'left', fontWeight: 600,
                          color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)',
                          fontSize: 10, textTransform: 'uppercase',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableOps.map((op, i) => (
                      <tr
                        key={`${op.program}-${op.line_number}-${i}`}
                        onClick={() => setSelectedOp(op)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedOp === op ? 'var(--bg-tertiary)' : 'transparent',
                        }}
                        onMouseEnter={e => { if (selectedOp !== op) e.currentTarget.style.backgroundColor = 'var(--bg-primary)' }}
                        onMouseLeave={e => { if (selectedOp !== op) e.currentTarget.style.backgroundColor = 'transparent' }}
                      >
                        <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 11 }}>{op.line_number}</td>
                        {!selectedProgram && (
                          <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: 11, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.program || '—'}</td>
                        )}
                        <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)', fontWeight: 600 }}>{op.verb}</td>
                        <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-color)' }}><CategoryBadge category={op.category || 'other'} /></td>
                        <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-color)', fontFamily: 'monospace', fontSize: 11 }}>
                          {op.record_name ? (
                            cbNames.has(op.record_name.toUpperCase()) && onNavigateToCopybook ? (
                              <span
                                onClick={(e) => { e.stopPropagation(); onNavigateToCopybook(op.record_name) }}
                                style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}
                                title={`View ${op.record_name} copybook fields`}
                              >{op.record_name}</span>
                            ) : (
                              <span style={{ color: 'var(--text-secondary)' }}>{op.record_name}</span>
                            )
                          ) : '—'}
                        </td>
                        <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontSize: 11 }}>{op.set_name || op.area_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right: Detail panel */}
      <div style={{
        width: 320, borderLeft: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)', flexShrink: 0,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {!selectedOp ? (
          <div style={{ padding: 20, overflowY: 'auto' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>Migration Summary</div>

            {/* Difficulty breakdown */}
            {MIGRATION_DIFFICULTY_ORDER.map(diff => {
              const colors = MIGRATION_COLORS[diff]
              const meta   = MIGRATION_META[migrationTarget][diff]
              const count  = diffGroups[diff].length
              if (!count) return null
              return (
                <div key={diff} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)' }}>{meta.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: colors.color }}>{count}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', width: 30, textAlign: 'right' }}>{pct(count)}%</span>
                </div>
              )
            })}

            {/* Migration readiness bar */}
            <div style={{ marginTop: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                Migration Readiness ({migrationTarget === 'db2' ? 'DB2' : 'SQL/JDBC'})
              </div>
              <div style={{ display: 'flex', height: 20, borderRadius: 4, overflow: 'hidden', background: 'var(--bg-primary)' }}>
                {MIGRATION_DIFFICULTY_ORDER.map(d => {
                  const w = pct(diffGroups[d].length)
                  if (!w) return null
                  return <div key={d} title={`${MIGRATION_META[migrationTarget][d].label}: ${w}%`} style={{ width: `${w}%`, background: MIGRATION_COLORS[d].color, opacity: 0.8 }} />
                })}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                {pct(diffGroups.easy.length)}% maps directly to {migrationTarget === 'db2' ? 'DB2 SQL' : 'SQL'}.
                {diffGroups.hard.length > 0 && ` ${pct(diffGroups.hard.length)}% requires ${migrationTarget === 'db2' ? 'stored procedure or logic rewrite' : 'manual rewrite'}.`}
              </div>
            </div>

            {/* Hottest records */}
            {topRecords.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Hottest Records</div>
                {topRecords.slice(0, 5).map(([rec, counts]) => (
                  <div key={rec} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
                    <span style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>{rec}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{counts.total} ops</span>
                  </div>
                ))}
              </>
            )}
          </div>
        ) : (
          <div style={{ overflowY: 'auto', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{selectedOp.verb}</span>
              <button onClick={() => setSelectedOp(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <XCircle size={16} />
              </button>
            </div>

            {/* Migration difficulty badge for this op */}
            {(() => {
              const d = getMigrationDifficulty(selectedOp)
              const c = MIGRATION_COLORS[d]
              const m = MIGRATION_META[migrationTarget][d]
              return (
                <div style={{
                  display: 'inline-block', padding: '3px 10px', borderRadius: 10, marginBottom: 12,
                  fontSize: 11, fontWeight: 600, color: c.color, background: c.bg,
                }}>
                  {m.label}
                </div>
              )
            })()}

            <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {selectedOp.program && (
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Program</span>
                  <div style={{ color: '#388bfd', fontFamily: 'monospace', marginTop: 2, fontWeight: 500 }}>{selectedOp.program}</div>
                </div>
              )}
              <div>
                <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Line Number</span>
                <div style={{ color: 'var(--text-primary)', fontFamily: 'monospace', marginTop: 2 }}>{selectedOp.line_number}</div>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Verb Type</span>
                <div style={{ color: 'var(--text-primary)', fontFamily: 'monospace', marginTop: 2 }}>{selectedOp.verb_type || selectedOp.verb}</div>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Category</span>
                <div style={{ marginTop: 2 }}><CategoryBadge category={selectedOp.category || 'other'} /></div>
              </div>
              {selectedOp.source_form && (
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Source Form</span>
                  <div style={{ color: 'var(--text-primary)', fontFamily: 'monospace', marginTop: 2 }}>{selectedOp.source_form}</div>
                </div>
              )}
              {selectedOp.record_name && (
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Record</span>
                  <div style={{ color: 'var(--text-primary)', fontFamily: 'monospace', marginTop: 2 }}>{selectedOp.record_name}</div>
                </div>
              )}
              {selectedOp.set_name && (
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Set</span>
                  <div style={{ color: 'var(--text-primary)', fontFamily: 'monospace', marginTop: 2 }}>{selectedOp.set_name}</div>
                </div>
              )}
              {selectedOp.area_name && (
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Area</span>
                  <div style={{ color: 'var(--text-primary)', fontFamily: 'monospace', marginTop: 2 }}>{selectedOp.area_name}</div>
                </div>
              )}
              {selectedOp.function_code && (
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Function Code</span>
                  <div style={{ color: 'var(--text-primary)', fontFamily: 'monospace', marginTop: 2 }}>{selectedOp.function_code}</div>
                </div>
              )}
              {/* SQL Equivalent */}
              {(() => {
                const ref = getDmlReference(selectedOp)
                return (
                  <>
                    {ref.sql && (
                      <div>
                        <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>SQL Equivalent</span>
                        <pre style={{
                          marginTop: 4, padding: 10, borderRadius: 6,
                          background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)',
                          fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap',
                          color: '#93c5fd', overflow: 'auto', maxHeight: 120,
                        }}>{ref.sql}</pre>
                      </div>
                    )}
                    {ref.java && (
                      <div>
                        <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Java Pattern</span>
                        <pre style={{
                          marginTop: 4, padding: 10, borderRadius: 6,
                          background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)',
                          fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap',
                          color: '#6ee7b7', overflow: 'auto', maxHeight: 120,
                        }}>{ref.java}</pre>
                      </div>
                    )}
                    {ref.desc && (
                      <div>
                        <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>What This Does</span>
                        <div style={{
                          marginTop: 4, padding: 10, borderRadius: 6,
                          background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
                          fontSize: 12, lineHeight: 1.6, color: 'var(--text-secondary)',
                        }}>{ref.desc}</div>
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Schema — Explorer + Migration Context + Heatmap
// ═══════════════════════════════════════════════════════════════════════════════

function classifyRecordMigration(record) {
  if (!record) return null
  const setCount = (record.member_of_sets || []).length
  const dmlVerbs = record.dml_verbs || {}
  const totalDml = record.total_dml || 0
  const fieldCount = record.field_count || null

  const complexVerbs = ['STORE', 'MODIFY', 'ERASE', 'CONNECT', 'DISCONNECT']
  const hasComplexVerbs = complexVerbs.some(v => (dmlVerbs[v] || 0) > 0)
  const currencyVerbs = ['ACCEPT', 'GET', 'KEEP']
  const hasCurrencyOps = currencyVerbs.some(v => (dmlVerbs[v] || 0) > 0)

  let difficulty, color, note
  if (setCount >= 3 || (setCount >= 2 && hasComplexVerbs) || (hasComplexVerbs && hasCurrencyOps)) {
    difficulty = 'Hard'
    color = '#ef4444'
    note = `Complex — member of ${setCount} set(s), requires FK design for each relationship`
    if (hasComplexVerbs) note += ', has mutation operations'
  } else if (setCount >= 1 || hasComplexVerbs) {
    difficulty = 'Moderate'
    color = '#f59e0b'
    note = setCount > 0 ? `Member of ${setCount} set(s), requires FK mapping` : 'Has mutation operations (STORE/MODIFY/ERASE)'
  } else {
    difficulty = 'Easy'
    color = '#10b981'
    note = totalDml > 0 ? 'Read-only access with simple retrieval patterns' : 'No DML access detected'
  }

  const tableNote = fieldCount
    ? `Maps to TABLE with ${fieldCount} columns`
    : 'Maps to TABLE (field count unknown)'

  return { difficulty, color, note, tableNote }
}

function buildSchemaExplorerData(schemaInventory, inferredSchema) {
  const si = schemaInventory || {}
  const is = inferredSchema || {}
  const hasRichData = !!(is.records && typeof is.records === 'object' && Object.keys(is.records).length > 0)

  // Build SR code reverse lookup from schema_inventory
  const srReverse = {}
  if (si.sr_codes) {
    for (const [code, recName] of Object.entries(si.sr_codes)) {
      srReverse[recName] = code
    }
  }

  // ── Records ──
  const records = []
  const seenRecords = new Set()

  if (hasRichData) {
    for (const [name, r] of Object.entries(is.records)) {
      const dmlVerbs = r.dml_verbs || {}
      const totalDml = Object.values(dmlVerbs).reduce((s, v) => s + v, 0)
      const rec = {
        type: 'record', name: r.name || name,
        sr_code: r.sr_code || srReverse[name] || null,
        total_bytes: r.total_bytes || null,
        field_count: r.field_count || null,
        has_layout: r.has_layout || false,
        key_fields: r.key_fields || [],
        dml_verbs: dmlVerbs,
        total_dml: totalDml,
        member_of_sets: r.member_of_sets || [],
        areas: r.areas || [],
      }
      rec.migration = classifyRecordMigration(rec)
      records.push(rec)
      seenRecords.add(name)
    }
  }
  // Add any records from schema_inventory not already covered
  const siRecords = Array.isArray(si.records) ? si.records : []
  for (const r of siRecords) {
    const name = typeof r === 'string' ? r : (r.name || r.record_name || '')
    if (name && !seenRecords.has(name)) {
      const rec = {
        type: 'record', name,
        sr_code: srReverse[name] || null,
        total_bytes: null, field_count: null, has_layout: false,
        key_fields: [], dml_verbs: {}, total_dml: 0,
        member_of_sets: [], areas: [],
      }
      rec.migration = classifyRecordMigration(rec)
      records.push(rec)
    }
  }

  // ── Sets ──
  const sets = []
  const seenSets = new Set()
  const richSets = (is.sets && typeof is.sets === 'object' && !Array.isArray(is.sets)) ? is.sets : {}

  for (const [name, s] of Object.entries(richSets)) {
    sets.push({
      type: 'set', name: s.set_name || name,
      owner: s.owner || null,
      members: s.members || [],
      member_count: (s.members || []).length,
      has_owner_access: s.has_owner_access || false,
      evidence: s.evidence || [],
    })
    seenSets.add(name)
  }
  const siSets = Array.isArray(si.sets) ? si.sets : []
  for (const s of siSets) {
    const name = typeof s === 'string' ? s : (s.name || s.set_name || '')
    if (name && !seenSets.has(name)) {
      sets.push({
        type: 'set', name,
        owner: null, members: [], member_count: 0,
        has_owner_access: false, evidence: [],
      })
    }
  }

  // ── Areas ──
  const areas = []
  const areaNames = new Set()
  // Collect area names from all sources
  const siAreas = Array.isArray(si.areas) ? si.areas : []
  const isAreas = Array.isArray(is.areas) ? is.areas : []
  for (const a of [...isAreas, ...siAreas]) {
    const name = typeof a === 'string' ? a : (a.name || '')
    if (name) areaNames.add(name)
  }
  // Also gather from records' areas fields
  for (const rec of records) {
    for (const a of rec.areas) areaNames.add(a)
  }
  for (const name of areaNames) {
    const recsInArea = records.filter(r => r.areas.includes(name)).map(r => r.name)
    areas.push({
      type: 'area', name,
      record_count: recsInArea.length,
      records: recsInArea,
    })
  }

  // ── KPIs ──
  const totalDml = hasRichData
    ? records.reduce((s, r) => s + r.total_dml, 0)
    : (is.total_dml_ops || null)
  const relationships = is.inferred_relationship_count || null

  // ── Meta ──
  const meta = {
    subschema: si.subschema || is.subschema || null,
    schema: si.schema || is.schema || null,
    mode: si.mode || is.mode || null,
    sr_codes: si.sr_codes || {},
  }

  return {
    records, sets, areas, meta, hasRichData,
    kpis: {
      recordCount: records.length,
      setCount: sets.length,
      areaCount: areas.length,
      totalDml,
      relationships,
    },
  }
}

function buildHeatmapData(inferredSchema, dmlOps) {
  const is = inferredSchema || {}
  const records = (is.records && typeof is.records === 'object') ? is.records : {}

  // Bar chart: records ranked by total DML with per-verb breakdown
  const recordBars = []
  for (const [name, r] of Object.entries(records)) {
    const verbs = r.dml_verbs || {}
    const total = Object.values(verbs).reduce((s, v) => s + v, 0)
    if (total > 0) recordBars.push({ name: r.name || name, total, verbs })
  }
  recordBars.sort((a, b) => b.total - a.total)

  // Program breakdown: per-record per-program access count
  const programAccess = {}  // { recordName: { programName: count } }
  const ops = Array.isArray(dmlOps) ? dmlOps : []
  for (const op of ops) {
    const rec = op.record_name
    const prog = op.program
    if (!rec || !prog) continue
    if (!programAccess[rec]) programAccess[rec] = {}
    programAccess[rec][prog] = (programAccess[rec][prog] || 0) + 1
  }

  return { recordBars, programAccess }
}

function SchemaTab({ schemaInventory, inferredSchema, dmlOps }) {
  const [selectedItem, setSelectedItem] = useState(null)
  const [searchText, setSearchText]     = useState('')
  const [activeScope, setActiveScope]   = useState('all')
  const [expandedGroups, setExpandedGroups] = useState(
    () => Object.fromEntries(SCHEMA_GROUP_ORDER.map(g => [g, true]))
  )
  const [showHeatmap, setShowHeatmap] = useState(false)

  const explorerData = useMemo(
    () => buildSchemaExplorerData(schemaInventory, inferredSchema),
    [schemaInventory, inferredSchema]
  )

  if (!schemaInventory && !inferredSchema) return <NotAvailable message="IDMS Analysis has not run yet." />

  const { records, sets, areas, meta, kpis, hasRichData } = explorerData

  // ── Filter + search ──
  const allItems = [...records, ...sets, ...areas]
  const filtered = allItems.filter(item => {
    if (activeScope === 'records' && item.type !== 'record') return false
    if (activeScope === 'sets'    && item.type !== 'set')    return false
    if (activeScope === 'areas'   && item.type !== 'area')   return false
    if (searchText) {
      const term = searchText.toLowerCase()
      return item.name.toLowerCase().includes(term) ||
        (item.owner || '').toLowerCase().includes(term) ||
        (item.sr_code || '').toLowerCase().includes(term)
    }
    return true
  })
  const grouped = {
    RECORDS: filtered.filter(i => i.type === 'record'),
    SETS:    filtered.filter(i => i.type === 'set'),
    AREAS:   filtered.filter(i => i.type === 'area'),
  }

  const handleNavigateTo = (type, name) => {
    const list = type === 'record' ? records : type === 'set' ? sets : areas
    const target = list.find(i => i.name === name)
    if (target) {
      setSelectedItem(target)
      // Expand the target's group
      const groupKey = type === 'record' ? 'RECORDS' : type === 'set' ? 'SETS' : 'AREAS'
      setExpandedGroups(prev => ({ ...prev, [groupKey]: true }))
    }
  }

  const toggleGroup = (g) => setExpandedGroups(prev => ({ ...prev, [g]: !prev[g] }))

  // ── KPI strip ──
  const kpiCards = [
    { label: 'Records',       value: kpis.recordCount, color: SCHEMA_GROUP_COLORS.RECORDS },
    { label: 'Sets',          value: kpis.setCount,    color: SCHEMA_GROUP_COLORS.SETS },
    { label: 'Areas',         value: kpis.areaCount,   color: SCHEMA_GROUP_COLORS.AREAS },
    { label: 'Total DML Ops', value: kpis.totalDml != null ? kpis.totalDml : 'N/A', color: '#8b5cf6' },
    { label: 'Relationships', value: kpis.relationships != null ? kpis.relationships : 'N/A', color: '#06b6d4' },
  ]

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', padding: '16px 20px 20px' }}>

        {/* ── KPI Strip ── */}
        <div className="ga-metrics-strip" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          {kpiCards.map(k => (
            <div key={k.label} className="ga-metric-card" style={{ flex: '0 1 auto', minWidth: 100 }}>
              <div className="ga-metric-value" style={{ color: k.color }}>{k.value}</div>
              <div className="ga-metric-label">{k.label}</div>
            </div>
          ))}
          {/* Schema metadata badges */}
          {meta.subschema && (
            <div style={{ marginLeft: 8, padding: '4px 10px', borderRadius: 6, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', fontSize: 11 }}>
              <span style={{ color: 'var(--text-muted)' }}>Subschema: </span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#3b82f6' }}>{meta.subschema}</span>
            </div>
          )}
          {meta.schema && (
            <div style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', fontSize: 11 }}>
              <span style={{ color: 'var(--text-muted)' }}>Schema: </span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#10b981' }}>{meta.schema}</span>
            </div>
          )}
          {meta.mode && (
            <div style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', fontSize: 11 }}>
              <span style={{ fontWeight: 600, color: '#f59e0b' }}>{meta.mode}</span>
            </div>
          )}
          {/* Heatmap button */}
          <button
            onClick={() => setShowHeatmap(true)}
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 6,
              border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)',
              color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}
          >
            <BarChart2 size={14} />
            Record Access Heatmap
          </button>
        </div>

        {/* ── Search + Scope ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search records, sets, areas..."
              style={{
                width: '100%', padding: '7px 10px 7px 30px', borderRadius: 6,
                border: '1px solid var(--border-color)', background: 'var(--bg-primary)',
                color: 'var(--text-primary)', fontSize: 12, outline: 'none',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {SCHEMA_SCOPE_CHIPS.map(chip => (
              <button key={chip.key} onClick={() => setActiveScope(chip.key)} style={{
                padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500,
                border: activeScope === chip.key ? '1px solid var(--accent-color)' : '1px solid var(--border-color)',
                background: activeScope === chip.key ? 'rgba(56,139,253,0.15)' : 'transparent',
                color: activeScope === chip.key ? 'var(--accent-color)' : 'var(--text-secondary)',
                cursor: 'pointer',
              }}>
                {chip.label}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>{filtered.length} items</span>
        </div>

        {/* ── Main: Left table + Right detail ── */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', gap: 16 }}>

          {/* ── Left: Grouped table ── */}
          <div style={{ flex: 7, overflow: 'auto' }}>
            {SCHEMA_GROUP_ORDER.map(groupKey => {
              const items = grouped[groupKey]
              if (!items || items.length === 0) return null
              const gColor = SCHEMA_GROUP_COLORS[groupKey]
              const isExpanded = expandedGroups[groupKey]

              return (
                <div key={groupKey} style={{ marginBottom: 12 }}>
                  {/* Group header */}
                  <button onClick={() => toggleGroup(groupKey)} style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', border: 'none', borderRadius: 6,
                    background: `${gColor}11`, cursor: 'pointer', textAlign: 'left',
                  }}>
                    {isExpanded ? <ChevronDown size={14} style={{ color: gColor }} /> : <ChevronRight size={14} style={{ color: gColor }} />}
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: gColor }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: gColor }}>{groupKey}</span>
                    <span style={{
                      fontSize: 10, padding: '1px 7px', borderRadius: 8,
                      background: `${gColor}22`, color: gColor, fontWeight: 600,
                    }}>{items.length}</span>
                  </button>

                  {isExpanded && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <th style={{ padding: '6px 12px', textAlign: 'left', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>NAME</th>
                          {groupKey === 'RECORDS' && (
                            <>
                              <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, width: 60 }}>SR</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, width: 60 }}>FIELDS</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, width: 60 }}>DML</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, width: 80 }}>MIGRATION</th>
                            </>
                          )}
                          {groupKey === 'SETS' && (
                            <>
                              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, width: 120 }}>OWNER</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, width: 70 }}>MEMBERS</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, width: 80 }}>OWNER ACC.</th>
                            </>
                          )}
                          {groupKey === 'AREAS' && (
                            <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, width: 80 }}>RECORDS</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, i) => {
                          const isSelected = selectedItem?.name === item.name && selectedItem?.type === item.type
                          return (
                            <tr
                              key={`${item.type}-${item.name}-${i}`}
                              onClick={() => setSelectedItem(item)}
                              style={{
                                cursor: 'pointer',
                                background: isSelected ? 'var(--bg-tertiary)' : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                                borderLeft: isSelected ? `3px solid ${gColor}` : '3px solid transparent',
                              }}
                              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-secondary)' }}
                              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}
                            >
                              <td style={{ padding: '7px 12px', fontSize: 12, fontFamily: 'monospace', color: 'var(--text-primary)' }}>{item.name}</td>
                              {groupKey === 'RECORDS' && (
                                <>
                                  <td style={{ padding: '7px 8px', textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{item.sr_code || '—'}</td>
                                  <td style={{ padding: '7px 8px', textAlign: 'center', fontSize: 11, color: 'var(--text-secondary)' }}>{item.field_count != null ? item.field_count : '—'}</td>
                                  <td style={{ padding: '7px 8px', textAlign: 'center', fontSize: 11, fontWeight: item.total_dml > 0 ? 600 : 400, color: item.total_dml > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>{item.total_dml || '—'}</td>
                                  <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                                    {item.migration ? (
                                      <span style={{
                                        fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                                        color: item.migration.color,
                                        background: `${item.migration.color}18`,
                                      }}>{item.migration.difficulty}</span>
                                    ) : '—'}
                                  </td>
                                </>
                              )}
                              {groupKey === 'SETS' && (
                                <>
                                  <td style={{ padding: '7px 8px', fontSize: 11, fontFamily: 'monospace', color: item.owner ? 'var(--text-primary)' : 'var(--text-muted)' }}>{item.owner || '—'}</td>
                                  <td style={{ padding: '7px 8px', textAlign: 'center', fontSize: 11, color: 'var(--text-secondary)' }}>{item.member_count}</td>
                                  <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                                    {item.has_owner_access ? (
                                      <CheckCircle2 size={13} style={{ color: '#10b981' }} />
                                    ) : (
                                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                                    )}
                                  </td>
                                </>
                              )}
                              {groupKey === 'AREAS' && (
                                <td style={{ padding: '7px 8px', textAlign: 'center', fontSize: 11, color: 'var(--text-secondary)' }}>{item.record_count}</td>
                              )}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )
            })}
            {filtered.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                No items match your search.
              </div>
            )}
          </div>

          {/* ── Right: Detail panel ── */}
          <div style={{
            flex: 3, overflow: 'auto', borderLeft: '1px solid var(--border-color)',
            paddingLeft: 16, display: 'flex', flexDirection: 'column',
          }}>
            {!selectedItem ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
                <Layers size={32} style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Select a record, set, or area to view details</span>
              </div>
            ) : (
              <SchemaDetailPanel item={selectedItem} meta={meta} hasRichData={hasRichData} onNavigate={handleNavigateTo} />
            )}
          </div>
        </div>
      </div>

      {/* ── Heatmap Modal ── */}
      {showHeatmap && (
        <SchemaHeatmapModal
          inferredSchema={inferredSchema}
          dmlOps={dmlOps}
          kpis={kpis}
          onClose={() => setShowHeatmap(false)}
        />
      )}
    </>
  )
}

/* ── Schema Detail Panel ── */
function SchemaDetailPanel({ item, meta, hasRichData, onNavigate }) {
  const typeBadge = { record: { label: 'RECORD', color: SCHEMA_GROUP_COLORS.RECORDS }, set: { label: 'SET', color: SCHEMA_GROUP_COLORS.SETS }, area: { label: 'AREA', color: SCHEMA_GROUP_COLORS.AREAS } }
  const badge = typeBadge[item.type] || typeBadge.record

  const SectionHeader = ({ children }) => (
    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 16, marginBottom: 6, borderBottom: '1px solid var(--border-color)', paddingBottom: 4 }}>
      {children}
    </div>
  )

  const DetailRow = ({ label, value, mono }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', fontSize: 12 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
    </div>
  )

  const ClickableTag = ({ name, type, color }) => (
    <span
      onClick={() => onNavigate(type, name)}
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11,
        fontFamily: 'monospace', cursor: 'pointer', marginRight: 4, marginBottom: 4,
        background: `${color}18`, color, border: `1px solid ${color}33`,
      }}
      title={`View ${type}: ${name}`}
    >
      {name}
    </span>
  )

  return (
    <div style={{ paddingBottom: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'monospace', color: 'var(--text-primary)' }}>{item.name}</span>
        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 700, color: badge.color, background: `${badge.color}18` }}>
          {badge.label}
        </span>
      </div>

      {/* ── Record detail ── */}
      {item.type === 'record' && (
        <>
          <SectionHeader>Metadata</SectionHeader>
          <DetailRow label="SR Code" value={item.sr_code || '—'} mono />
          {item.total_bytes != null && <DetailRow label="Total Bytes" value={item.total_bytes.toLocaleString()} />}
          <DetailRow label="Field Count" value={item.field_count != null ? item.field_count : '—'} />
          <DetailRow label="Has Layout" value={item.has_layout ? 'Yes' : 'No'} />

          {item.key_fields.length > 0 && (
            <>
              <SectionHeader>Key Fields</SectionHeader>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {item.key_fields.map(f => (
                  <span key={f} style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11,
                    fontFamily: 'monospace', background: 'rgba(59,130,246,0.1)', color: '#93c5fd',
                  }}>{f}</span>
                ))}
              </div>
            </>
          )}

          {hasRichData && Object.keys(item.dml_verbs).length > 0 && (
            <>
              <SectionHeader>DML Verb Breakdown</SectionHeader>
              {(() => {
                const verbs = Object.entries(item.dml_verbs).sort((a, b) => b[1] - a[1])
                const maxVerb = verbs.length ? verbs[0][1] : 1
                return verbs.map(([verb, count]) => (
                  <div key={verb} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ width: 70, fontSize: 10, fontFamily: 'monospace', color: VERB_COLORS[verb] || '#6b7280', fontWeight: 600, textAlign: 'right' }}>{verb}</span>
                    <div style={{ flex: 1, height: 12, borderRadius: 3, background: 'var(--bg-primary)', overflow: 'hidden' }}>
                      <div style={{ width: `${(count / maxVerb) * 100}%`, height: '100%', borderRadius: 3, background: VERB_COLORS[verb] || '#6b7280', opacity: 0.6 }} />
                    </div>
                    <span style={{ width: 28, fontSize: 10, color: 'var(--text-secondary)', textAlign: 'right' }}>{count}</span>
                  </div>
                ))
              })()}
            </>
          )}

          {item.member_of_sets.length > 0 && (
            <>
              <SectionHeader>Set Membership</SectionHeader>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {item.member_of_sets.map(s => <ClickableTag key={s} name={s} type="set" color={SCHEMA_GROUP_COLORS.SETS} />)}
              </div>
            </>
          )}

          {item.areas.length > 0 && (
            <>
              <SectionHeader>Areas</SectionHeader>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {item.areas.map(a => <ClickableTag key={a} name={a} type="area" color={SCHEMA_GROUP_COLORS.AREAS} />)}
              </div>
            </>
          )}

          {item.migration && (
            <>
              <SectionHeader>Migration Assessment</SectionHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: item.migration.color }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: item.migration.color }}>{item.migration.difficulty}</span>
              </div>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 4, fontWeight: 500 }}>{item.migration.tableNote}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{item.migration.note}</div>
            </>
          )}

          {!hasRichData && (
            <div style={{ marginTop: 16, padding: 12, borderRadius: 6, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', fontSize: 11, color: '#93c5fd' }}>
              Run Data Architecture for detailed DML analysis, key fields, and migration assessment.
            </div>
          )}
        </>
      )}

      {/* ── Set detail ── */}
      {item.type === 'set' && (
        <>
          <SectionHeader>Relationship</SectionHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)' }}>Owner Record</span>
            {item.owner ? <ClickableTag name={item.owner} type="record" color={SCHEMA_GROUP_COLORS.RECORDS} /> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
          </div>
          <DetailRow label="Member Count" value={item.member_count} />
          <DetailRow label="Has Owner Access" value={item.has_owner_access ? 'Yes' : 'No'} />

          {item.members.length > 0 && (
            <>
              <SectionHeader>Member Records</SectionHeader>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {item.members.map(m => <ClickableTag key={m} name={m} type="record" color={SCHEMA_GROUP_COLORS.RECORDS} />)}
              </div>
            </>
          )}

          {item.evidence.length > 0 && (
            <>
              <SectionHeader>Evidence (DML Proof)</SectionHeader>
              <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                {item.evidence.map((e, i) => (
                  <div key={i} style={{
                    padding: '4px 8px', marginBottom: 2, borderRadius: 4,
                    background: 'var(--bg-primary)', fontSize: 10, fontFamily: 'monospace',
                    color: 'var(--text-secondary)', lineHeight: 1.6, wordBreak: 'break-all',
                  }}>{e}</div>
                ))}
              </div>
            </>
          )}

          <SectionHeader>Migration Assessment</SectionHeader>
          {item.owner && item.members.length > 0 ? (
            <>
              <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 4, fontWeight: 500 }}>
                Maps to FOREIGN KEY: {item.members.join(', ')} &rarr; {item.owner}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                One-to-many: {item.owner} (1) &rarr; {item.members.join(', ')} (N)
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {item.owner ? 'Owner identified but no members inferred.' : 'Relationship not fully inferred — run Data Architecture for deeper analysis.'}
            </div>
          )}
        </>
      )}

      {/* ── Area detail ── */}
      {item.type === 'area' && (
        <>
          <SectionHeader>Records in Area</SectionHeader>
          {item.records.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {item.records.map(r => <ClickableTag key={r} name={r} type="record" color={SCHEMA_GROUP_COLORS.RECORDS} />)}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>No records associated with this area.</div>
          )}

          <SectionHeader>Migration Assessment</SectionHeader>
          <div style={{ fontSize: 11, color: '#93c5fd', marginBottom: 4, fontWeight: 500 }}>
            Maps to TABLESPACE or DATABASE SCHEMA
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            IDMS areas map to physical storage groupings. In a relational database, this corresponds to a tablespace or schema grouping containing {item.record_count} table(s).
          </div>
        </>
      )}

      {/* SR Codes reference (always show if available) */}
      {meta.sr_codes && Object.keys(meta.sr_codes).length > 0 && (
        <>
          <SectionHeader>SR Code Reference</SectionHeader>
          <div style={{ maxHeight: 140, overflowY: 'auto' }}>
            {Object.entries(meta.sr_codes).map(([code, desc]) => (
              <div key={code} style={{ fontSize: 11, marginBottom: 2, display: 'flex', gap: 6 }}>
                <span style={{ fontFamily: 'monospace', color: '#f59e0b', fontWeight: 600, minWidth: 30 }}>{code}</span>
                <span
                  style={{ color: 'var(--text-secondary)', cursor: 'pointer' }}
                  onClick={() => onNavigate('record', desc)}
                  title={`View record: ${desc}`}
                >{desc}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/* ── Schema Heatmap Modal ── */
function SchemaHeatmapModal({ inferredSchema, dmlOps, kpis, onClose }) {
  const heatData = useMemo(
    () => buildHeatmapData(inferredSchema, dmlOps),
    [inferredSchema, dmlOps]
  )

  const { recordBars, programAccess } = heatData
  const [expandedRecord, setExpandedRecord] = useState(null)

  if (recordBars.length === 0) {
    return (
      <div className="ga-graph-modal-overlay" onClick={onClose}>
        <div className="ga-graph-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500, height: 'auto' }}>
          <div className="ga-graph-modal-header">
            <div className="ga-graph-modal-title"><BarChart2 size={18} /> Record Access Heatmap</div>
            <button className="ga-graph-modal-close" onClick={onClose}><XCircle size={18} /></button>
          </div>
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No DML verb data available. Run Data Architecture to generate record access analysis.
          </div>
        </div>
      </div>
    )
  }

  const maxTotal = recordBars[0]?.total || 1
  const allVerbs = new Set()
  for (const bar of recordBars) for (const v of Object.keys(bar.verbs)) allVerbs.add(v)
  const verbList = [...allVerbs]

  return (
    <div className="ga-graph-modal-overlay" onClick={onClose}>
      <div className="ga-graph-modal" onClick={e => e.stopPropagation()}>
        <div className="ga-graph-modal-header">
          <div className="ga-graph-modal-title"><BarChart2 size={18} /> Record Access Heatmap</div>
          <span className="ga-graph-modal-badge">{kpis.recordCount} records</span>
          <button className="ga-graph-modal-close" onClick={onClose}><XCircle size={18} /></button>
        </div>

        <div className="ga-graph-modal-body" style={{ padding: 24, overflowY: 'auto' }}>

          {/* ── Section 1: Record Access by Verb ── */}
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>
              Record Access by DML Verb
            </h3>
            {recordBars.map(bar => {
              const hasPrograms = programAccess[bar.name] && Object.keys(programAccess[bar.name]).length > 0
              const isExpanded = expandedRecord === bar.name
              return (
                <div key={bar.name} style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
                    {/* Expand toggle for program breakdown */}
                    {hasPrograms ? (
                      <button onClick={() => setExpandedRecord(isExpanded ? null : bar.name)} style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-muted)',
                      }}>
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                    ) : <span style={{ width: 12 }} />}
                    <span style={{ width: 140, fontSize: 11, fontFamily: 'monospace', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {bar.name}
                    </span>
                    <div style={{ flex: 1, display: 'flex', height: 18, borderRadius: 3, overflow: 'hidden', background: 'var(--bg-primary)' }}>
                      {verbList.map(verb => {
                        const count = bar.verbs[verb] || 0
                        if (count === 0) return null
                        return (
                          <div
                            key={verb}
                            title={`${verb}: ${count}`}
                            style={{
                              width: `${(count / maxTotal) * 100}%`,
                              background: VERB_COLORS[verb] || '#6b7280',
                              opacity: 0.7,
                              minWidth: count > 0 ? 2 : 0,
                            }}
                          />
                        )
                      })}
                    </div>
                    <span style={{ width: 36, fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right', fontWeight: 600 }}>{bar.total}</span>
                  </div>

                  {/* Program breakdown (expanded) */}
                  {isExpanded && hasPrograms && (
                    <div style={{ marginLeft: 162, marginBottom: 8, paddingLeft: 12, borderLeft: '2px solid var(--border-color)' }}>
                      {Object.entries(programAccess[bar.name])
                        .sort((a, b) => b[1] - a[1])
                        .map(([prog, count]) => {
                          const progMax = Object.values(programAccess[bar.name]).reduce((a, b) => Math.max(a, b), 1)
                          return (
                            <div key={prog} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                              <span style={{ width: 120, fontSize: 10, fontFamily: 'monospace', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prog}</span>
                              <div style={{ flex: 1, height: 10, borderRadius: 2, background: 'var(--bg-primary)', overflow: 'hidden' }}>
                                <div style={{ width: `${(count / progMax) * 100}%`, height: '100%', borderRadius: 2, background: '#388bfd', opacity: 0.5 }} />
                              </div>
                              <span style={{ width: 28, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>{count}</span>
                            </div>
                          )
                        })}
                    </div>
                  )}
                </div>
              )
            })}

            {/* Legend */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, fontSize: 10, color: 'var(--text-muted)' }}>
              {verbList.map(v => (
                <span key={v} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: VERB_COLORS[v] || '#6b7280', opacity: 0.7 }} />
                  {v}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Data Architecture (ERD)
// ═══════════════════════════════════════════════════════════════════════════════

function DataArchitectureTab({ inferredSchema, analysisGraph }) {
  const [selectedNode, setSelectedNode] = useState(null)

  if (!inferredSchema && !analysisGraph) return <NotAvailable message="Data Architecture has not run yet." />

  // Build ReactFlow nodes/edges from analysis_graph
  const graph = analysisGraph || {}
  const graphNodes = graph.nodes || []
  const graphEdges = graph.edges || []

  const nodeColorMap = {
    record: { bg: '#1e3a5f', border: '#3b82f6', color: '#93c5fd' },
    set:    { bg: '#1a3a2a', border: '#10b981', color: '#6ee7b7' },
    area:   { bg: '#3a2a1a', border: '#f59e0b', color: '#fcd34d' },
    file:   { bg: '#2a2a2a', border: '#6b7280', color: '#d1d5db' },
  }

  // Layout: simple grid
  const cols = Math.max(4, Math.ceil(Math.sqrt(graphNodes.length)))
  const rfNodes = graphNodes.map((n, i) => {
    const type = (n.type || 'record').toLowerCase()
    const colors = nodeColorMap[type] || nodeColorMap.record
    return {
      id: n.id || `node-${i}`,
      position: { x: (i % cols) * 200 + 50, y: Math.floor(i / cols) * 120 + 50 },
      data: {
        label: (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: colors.color, textTransform: 'uppercase', marginBottom: 2 }}>{type}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{n.name || n.label || n.id}</div>
          </div>
        ),
      },
      style: {
        background: colors.bg, border: `1px solid ${colors.border}`,
        borderRadius: 8, padding: '8px 12px', minWidth: 100,
      },
    }
  })

  const rfEdges = graphEdges.map((e, i) => ({
    id: `edge-${i}`,
    source: e.source,
    target: e.target,
    label: e.label || e.relationship || '',
    style: { stroke: 'var(--border-color)' },
    labelStyle: { fontSize: 9, fill: 'var(--text-muted)' },
    type: 'smoothstep',
  }))

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <div style={{ flex: 1 }}>
        <ReactFlowProvider>
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            onNodeClick={(_, node) => setSelectedNode(node)}
            fitView
            style={{ background: 'var(--bg-primary)' }}
          >
            <Background color="var(--border-color)" gap={20} />
            <Controls />
            <MiniMap style={{ background: 'var(--bg-secondary)' }} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>

      {/* Inferred schema detail panel */}
      {inferredSchema && (
        <div style={{
          width: 280, borderLeft: '1px solid var(--border-color)',
          overflowY: 'auto', padding: 16, background: 'var(--bg-secondary)', flexShrink: 0,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
            Inferred Schema
          </div>
          {(inferredSchema.relationships || []).map((rel, i) => (
            <div key={i} style={{
              padding: 8, marginBottom: 6, borderRadius: 6,
              background: 'var(--bg-tertiary)', fontSize: 11,
            }}>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {rel.owner || rel.source} → {rel.member || rel.target}
              </div>
              {rel.set_name && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>Set: {rel.set_name}</div>}
              {rel.relationship && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{rel.relationship}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Copybooks — Field Explorer with Migration Context
// ═══════════════════════════════════════════════════════════════════════════════

function CopybooksTab({ copybooks, schemaInventory, initialCopybook }) {
  const [selectedCb, setSelectedCb] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [activeScope, setActiveScope] = useState('all')
  const [sourceHeight, setSourceHeight] = useState(200)
  const [isDragging, setIsDragging] = useState(false)
  const dragRef = useRef(null)
  const containerRef = useRef(null)

  // Drag-to-resize handlers for the raw COBOL source panel
  useEffect(() => {
    if (!isDragging) return
    const handleMouseMove = (e) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const newHeight = rect.bottom - e.clientY - 8 // 8px for the handle
      setSourceHeight(Math.max(40, Math.min(newHeight, rect.height - 100)))
    }
    const handleMouseUp = () => setIsDragging(false)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  // Auto-select copybook when navigating from DML Operations
  useEffect(() => {
    if (!initialCopybook) return
    setSelectedCb(initialCopybook)  // name-based matching in activeCb
  }, [initialCopybook])

  if (!copybooks) return <NotAvailable message="IDMS Analysis has not run yet." />

  const cbs = Array.isArray(copybooks) ? copybooks : (copybooks?.copybooks || [])

  // Build schema record names for classification
  const schemaRecords = useMemo(() => {
    if (!schemaInventory) return null
    const recs = schemaInventory.records || []
    if (Array.isArray(recs)) return recs.map(r => typeof r === 'string' ? r : r.name)
    return Object.keys(recs)
  }, [schemaInventory])

  // Classify and enrich copybooks
  const enriched = useMemo(() => cbs.map(cb => {
    const cbType = classifyCopybook(cb, schemaRecords)
    const fieldCount = cb.field_count || (cb.fields || []).length
    return { ...cb, cbType, fieldCount }
  }), [cbs, schemaRecords])

  // KPIs
  const kpis = useMemo(() => {
    let totalFields = 0, totalBytes = 0, verifiedCount = 0
    const typeCounts = { 'IDMS Record': 0, 'Working Storage': 0, 'Infrastructure': 0 }
    for (const cb of enriched) {
      totalFields += cb.fieldCount
      totalBytes += cb.total_bytes || 0
      if (cb.verified) verifiedCount++
      typeCounts[cb.cbType] = (typeCounts[cb.cbType] || 0) + 1
    }
    return { totalFields, totalBytes, verifiedCount, typeCounts }
  }, [enriched])

  // Filter
  const filtered = useMemo(() => {
    let list = enriched
    if (activeScope === 'idms') list = list.filter(cb => cb.cbType === 'IDMS Record')
    else if (activeScope === 'work') list = list.filter(cb => cb.cbType === 'Working Storage')
    else if (activeScope === 'infra') list = list.filter(cb => cb.cbType === 'Infrastructure')
    if (searchText) {
      const q = searchText.toUpperCase()
      list = list.filter(cb => cb.name.toUpperCase().includes(q))
    }
    return list
  }, [enriched, activeScope, searchText])

  const activeCb = useMemo(() => {
    if (selectedCb) {
      // Match by name since enrichment creates new objects
      const match = filtered.find(cb => cb.name === (typeof selectedCb === 'string' ? selectedCb : selectedCb.name))
      if (match) return match
    }
    return filtered[0] || null
  }, [selectedCb, filtered])

  // Flatten fields for display — handle nested children or fallback to raw_cobol parsing
  const displayFields = useMemo(() => {
    if (!activeCb) return []
    if (activeCb.fields && activeCb.fields.length > 0) {
      return flattenFields(activeCb.fields, 0)
    }
    // Fallback: parse from raw COBOL source
    return parseFieldsFromRawCobol(activeCb.raw_cobol)
  }, [activeCb])

  const levelColors = {
    '1': '#3b82f6', '01': '#3b82f6',
    '2': '#6366f1', '02': '#6366f1',
    '3': '#8b5cf6', '03': '#8b5cf6',
    '5': '#06b6d4', '05': '#06b6d4',
    '10': '#10b981',
    '15': '#f59e0b',
    '20': '#f97316',
    '25': '#ef4444',
    '49': '#ef4444',
    '88': '#ec4899',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {/* KPI Strip */}
      <div className="ga-metrics-strip" style={{ flexShrink: 0 }}>
        {[
          { label: 'Copybooks', value: enriched.length },
          { label: 'Total Fields', value: kpis.totalFields.toLocaleString() },
          { label: 'Total Bytes', value: kpis.totalBytes.toLocaleString() },
          { label: 'Verified', value: `${kpis.verifiedCount} / ${enriched.length}` },
          { label: 'IDMS Records', value: kpis.typeCounts['IDMS Record'] },
        ].map((m, i) => (
          <div key={i} className="ga-metric-card">
            <div className="ga-metric-value">{m.value}</div>
            <div className="ga-metric-label">{m.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left: Copybook list */}
        <div style={{
          width: 260, borderRight: '1px solid var(--border-color)',
          overflowY: 'auto', flexShrink: 0, background: 'var(--bg-secondary)',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Search */}
          <div style={{ padding: '8px 10px 4px' }}>
            <input
              type="text"
              placeholder="Search copybooks..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{
                width: '100%', padding: '6px 10px', fontSize: 12,
                background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
                borderRadius: 6, color: 'var(--text-primary)', outline: 'none',
              }}
            />
          </div>

          {/* Scope chips */}
          <div style={{ display: 'flex', gap: 4, padding: '4px 10px 8px', flexWrap: 'wrap' }}>
            {CB_SCOPE_CHIPS.map(ch => (
              <button
                key={ch.key}
                onClick={() => setActiveScope(ch.key)}
                style={{
                  padding: '3px 8px', fontSize: 10, borderRadius: 10, cursor: 'pointer',
                  border: activeScope === ch.key ? '1px solid #3b82f6' : '1px solid var(--border-color)',
                  background: activeScope === ch.key ? 'rgba(59,130,246,0.15)' : 'transparent',
                  color: activeScope === ch.key ? '#93c5fd' : 'var(--text-muted)',
                }}
              >
                {ch.label}
              </button>
            ))}
            <span style={{ fontSize: 10, color: 'var(--text-muted)', alignSelf: 'center', marginLeft: 'auto' }}>
              {filtered.length} items
            </span>
          </div>

          {/* Copybook list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filtered.map((cb, i) => {
              const isActive = activeCb === cb
              const typeColor = CB_TYPE_COLORS[cb.cbType] || '#6b7280'
              return (
                <button
                  key={i}
                  onClick={() => setSelectedCb(cb)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    width: '100%', padding: '7px 10px', border: 'none', cursor: 'pointer',
                    fontSize: 11, textAlign: 'left',
                    background: isActive ? 'var(--bg-tertiary)' : 'transparent',
                    color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontWeight: isActive ? 600 : 400,
                    borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                  }}
                >
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: typeColor,
                  }} />
                  <span style={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {cb.name}
                  </span>
                  <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                    {cb.verified && <CheckCircle2 size={11} style={{ color: '#3fb950' }} />}
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', minWidth: 24, textAlign: 'right' }}>{cb.fieldCount}f</span>
                  </span>
                </button>
              )
            })}
          </div>

          {/* Bottom: Type distribution */}
          <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border-color)', fontSize: 10, color: 'var(--text-muted)' }}>
            {Object.entries(kpis.typeCounts).filter(([, v]) => v > 0).map(([type, count]) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: CB_TYPE_COLORS[type] }} />
                <span>{type}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 600 }}>{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Field explorer */}
        <div ref={containerRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {activeCb ? (
            <>
              {/* Header */}
              <div style={{
                padding: '10px 16px', borderBottom: '1px solid var(--border-color)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'var(--bg-secondary)', flexShrink: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                    {activeCb.name}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                    background: `${CB_TYPE_COLORS[activeCb.cbType]}22`,
                    color: CB_TYPE_COLORS[activeCb.cbType],
                    border: `1px solid ${CB_TYPE_COLORS[activeCb.cbType]}44`,
                  }}>
                    {activeCb.cbType}
                  </span>
                  {activeCb.verified && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#3fb950' }}>
                      <CheckCircle2 size={12} /> Verified
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: 'var(--text-muted)' }}>
                  {activeCb.total_bytes > 0 && <span>{activeCb.total_bytes} bytes</span>}
                  <span>{activeCb.fieldCount} fields</span>
                  {activeCb.level_88_count > 0 && <span>{activeCb.level_88_count} conditions</span>}
                </div>
              </div>

              {/* Fields table */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {displayFields.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: 'var(--bg-secondary)', zIndex: 1 }}>
                        {['Lvl', 'Name', 'PIC', 'SQL Type', 'Offset', 'Size', 'Flags'].map(h => (
                          <th key={h} style={{
                            padding: '6px 10px', textAlign: 'left', fontWeight: 600,
                            color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)',
                            fontSize: 11, textTransform: 'uppercase',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayFields.map((f, i) => {
                        const lvl = String(f.level || '')
                        const indent = (f._depth || 0) * 16
                        const lColor = levelColors[lvl] || levelColors[String(parseInt(lvl))] || 'var(--text-muted)'
                        const pic = f.pic || ''
                        const usage = f.usage || ''
                        const sqlType = picToSqlType(pic, usage)
                        const isGroup = !pic && f.name !== 'FILLER'
                        const offset = f.byte_offset != null ? f.byte_offset : (f.offset != null ? f.offset : null)
                        const size = f.byte_size != null ? f.byte_size : (f.size != null ? f.size : null)

                        return (
                          <tr key={i} style={{ background: isGroup ? 'rgba(59,130,246,0.03)' : 'transparent' }}>
                            <td style={{ padding: '3px 10px', borderBottom: '1px solid var(--border-color)', color: lColor, fontWeight: 600, fontFamily: 'monospace', width: 40 }}>
                              {lvl.padStart(2, '0')}
                            </td>
                            <td style={{
                              padding: '3px 10px', borderBottom: '1px solid var(--border-color)',
                              paddingLeft: indent + 10, color: 'var(--text-primary)', fontFamily: 'monospace',
                              fontWeight: isGroup ? 600 : 400,
                            }}>
                              {f.name}
                              {isGroup && <span style={{ fontSize: 9, color: 'var(--text-muted)', marginLeft: 6 }}>GROUP</span>}
                            </td>
                            <td style={{ padding: '3px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                              {pic || '—'}
                            </td>
                            <td style={{ padding: '3px 10px', borderBottom: '1px solid var(--border-color)', color: sqlType ? '#10b981' : 'var(--text-muted)', fontSize: 11 }}>
                              {sqlType || (isGroup ? '—' : '—')}
                            </td>
                            <td style={{ padding: '3px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontFamily: 'monospace', width: 60 }}>
                              {offset != null ? offset : '—'}
                            </td>
                            <td style={{ padding: '3px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontFamily: 'monospace', width: 50 }}>
                              {size != null ? size : '—'}
                            </td>
                            <td style={{ padding: '3px 10px', borderBottom: '1px solid var(--border-color)', fontSize: 10 }}>
                              {f.redefines && (
                                <span style={{ padding: '1px 5px', borderRadius: 4, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', marginRight: 4 }}>REDEF</span>
                              )}
                              {f.occurs > 0 && (
                                <span style={{ padding: '1px 5px', borderRadius: 4, background: 'rgba(139,92,246,0.15)', color: '#8b5cf6' }}>OCCURS({f.occurs})</span>
                              )}
                              {!f.redefines && !f.occurs && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    No parsed fields available — view raw COBOL source below
                  </div>
                )}
              </div>

              {/* Bottom: Raw COBOL source (drag-to-resize) */}
              {activeCb.raw_cobol && (
                <>
                  {/* Drag handle */}
                  <div
                    ref={dragRef}
                    onMouseDown={(e) => { e.preventDefault(); setIsDragging(true) }}
                    style={{
                      height: 8, flexShrink: 0,
                      cursor: 'row-resize',
                      background: isDragging ? 'rgba(59,130,246,0.3)' : 'var(--bg-secondary)',
                      borderTop: `2px solid ${isDragging ? '#3b82f6' : 'var(--border-color)'}`,
                      borderBottom: `1px solid var(--border-color)`,
                      transition: isDragging ? 'none' : 'border-color 0.15s, background 0.15s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onMouseEnter={e => { if (!isDragging) { e.currentTarget.style.borderTopColor = '#3b82f6'; e.currentTarget.style.background = 'rgba(59,130,246,0.08)' } }}
                    onMouseLeave={e => { if (!isDragging) { e.currentTarget.style.borderTopColor = 'var(--border-color)'; e.currentTarget.style.background = 'var(--bg-secondary)' } }}
                  >
                    {/* Grip dots */}
                    <div style={{ display: 'flex', gap: 3 }}>
                      {[0,1,2,3,4].map(k => (
                        <div key={k} style={{ width: 3, height: 3, borderRadius: '50%', background: isDragging ? '#3b82f6' : 'var(--text-muted)', opacity: 0.5 }} />
                      ))}
                    </div>
                  </div>
                  <div style={{
                    height: sourceHeight, flexShrink: 0, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    userSelect: isDragging ? 'none' : 'auto',
                  }}>
                    <div style={{
                      padding: '6px 12px', fontSize: 10, fontWeight: 600,
                      color: 'var(--text-muted)', textTransform: 'uppercase',
                      borderBottom: '1px solid var(--border-color)',
                      background: 'var(--bg-secondary)', flexShrink: 0,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <Code2 size={12} />
                      Raw COBOL Source ({activeCb.raw_cobol.split('\n').length} lines)
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      <pre style={{
                        margin: 0, padding: '8px 12px', fontSize: 11, fontFamily: 'monospace',
                        color: 'var(--text-primary)', whiteSpace: 'pre', lineHeight: 1.5,
                      }}>
                        {activeCb.raw_cobol}
                      </pre>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Select a copybook</div>
          )}
        </div>
      </div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Compatibility (Findings + Transform)
// ═══════════════════════════════════════════════════════════════════════════════

function CompatibilityTab({ findings, transformChanges }) {
  const [subView, setSubView] = useState('findings')
  const [selectedFinding, setSelectedFinding] = useState(null)

  if (!findings && !transformChanges) return <NotAvailable message="IDMS Compatibility has not run yet." />

  // cmpr2_findings.json is a flat array; transform_changes.json is a flat array
  const allFindings = Array.isArray(findings) ? findings : (findings?.findings || [])
  const allChanges  = Array.isArray(transformChanges) ? transformChanges : (transformChanges?.changes || [])

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toggle bar */}
      <div style={{
        display: 'flex', gap: 0, padding: '8px 16px',
        borderBottom: '1px solid var(--border-color)',
      }}>
        {['findings', 'transform'].map(v => (
          <button
            key={v}
            onClick={() => setSubView(v)}
            style={{
              padding: '6px 16px', border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: subView === v ? 600 : 400,
              background: subView === v ? 'var(--bg-tertiary)' : 'transparent',
              color: subView === v ? 'var(--text-primary)' : 'var(--text-muted)',
              borderRadius: 6,
            }}
          >
            {v === 'findings' ? `Findings (${allFindings.length})` : `Transform (${allChanges.length})`}
          </button>
        ))}
      </div>

      {subView === 'findings' ? (
        /* Findings table */
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: 'var(--bg-secondary)', zIndex: 1 }}>
                {['Rule', 'Severity', 'Line', 'Paragraph', 'Detail', 'Auto-Fix'].map(h => (
                  <th key={h} style={{
                    padding: '8px 10px', textAlign: 'left', fontWeight: 600,
                    color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)',
                    fontSize: 11, textTransform: 'uppercase',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allFindings.map((f, i) => (
                <tr key={i} style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedFinding(f)}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)', fontFamily: 'monospace', fontWeight: 500 }}>
                    {f.rule || f.rule_id || '—'}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-color)' }}>
                    <SeverityBadge severity={f.severity || f.risk || 'info'} />
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {f.line_number || f.line || '—'}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                    {f.paragraph || '—'}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.detail || f.description || f.message || '—'}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-color)' }}>
                    {f.auto_fix ? (
                      <CheckCircle2 size={14} style={{ color: '#3fb950' }} />
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Manual</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Transform view */
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {allChanges.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No transform changes available.</div>
          ) : (
            <div style={{ padding: 16 }}>
              {allChanges.map((ch, i) => (
                <div key={i} style={{
                  padding: 12, marginBottom: 8, borderRadius: 8,
                  background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
                        Line {ch.line_number || ch.line || '?'}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {ch.rule || ch.rule_id || ''}
                      </span>
                    </div>
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 10,
                      background: ch.status === 'applied' ? 'rgba(63,185,80,0.15)' : 'rgba(245,158,11,0.15)',
                      color: ch.status === 'applied' ? '#3fb950' : '#f59e0b',
                      fontWeight: 600,
                    }}>{ch.status || 'pending'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    {ch.description || ch.detail || ''}
                  </div>
                  {/* Before/After diff */}
                  {(ch.before || ch.original) && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: '#ef4444', marginBottom: 4, fontWeight: 600 }}>BEFORE</div>
                        <pre style={{
                          margin: 0, padding: 8, borderRadius: 4,
                          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
                          fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', color: 'var(--text-primary)',
                        }}>{ch.before || ch.original}</pre>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: '#3fb950', marginBottom: 4, fontWeight: 600 }}>AFTER</div>
                        <pre style={{
                          margin: 0, padding: 8, borderRadius: 4,
                          background: 'rgba(63,185,80,0.06)', border: '1px solid rgba(63,185,80,0.15)',
                          fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', color: 'var(--text-primary)',
                        }}>{ch.after || ch.transformed}</pre>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: V6 Output
// ═══════════════════════════════════════════════════════════════════════════════

function V6OutputTab({ v6Manifest, compatibilityResults }) {
  const [selectedProgram, setSelectedProgram] = useState(null)
  const [activeSubTab, setActiveSubTab] = useState('source')

  // Build sorted program list from manifest (hooks must come before any early return)
  const programList = useMemo(() => {
    if (!v6Manifest || typeof v6Manifest !== 'object') return []
    return Object.entries(v6Manifest)
      .map(([stem, info]) => ({ stem, ...info }))
      .sort((a, b) => {
        // Changed programs first, then by lines_changed desc
        if (a.is_unchanged !== b.is_unchanged) return a.is_unchanged ? 1 : -1
        return (b.lines_changed || 0) - (a.lines_changed || 0)
      })
  }, [v6Manifest])

  // Auto-select first program
  const activeStem = selectedProgram || (programList[0]?.stem || null)

  // Get emitter data for selected program
  const activeEmitter = useMemo(() => {
    if (!activeStem || !compatibilityResults) return null
    const prog = compatibilityResults[activeStem]
    return prog?.emitter || null
  }, [activeStem, compatibilityResults])

  // Get manifest info for selected program
  const activeInfo = activeStem && v6Manifest ? v6Manifest[activeStem] : null

  // KPI aggregates
  const kpis = useMemo(() => {
    const total = programList.length
    const changed = programList.filter(p => !p.is_unchanged).length
    const unchanged = programList.filter(p => p.is_unchanged).length
    const totalLinesChanged = programList.reduce((s, p) => s + (p.lines_changed || 0), 0)
    return { total, changed, unchanged, totalLinesChanged }
  }, [programList])

  // Early return AFTER all hooks
  if (!v6Manifest && !compatibilityResults) return <NotAvailable message="COBOL Version Upgrade has not run yet." />

  // Sub-tab definitions
  const subTabs = [
    { key: 'source',  label: 'V6 Source',      available: true },
    { key: 'changes', label: 'Change Report',  available: activeInfo?.has_change_report },
    { key: 'jcl',     label: 'JCL Report',     available: activeInfo?.has_jcl_report },
    { key: 'dml',     label: 'DML Migration',  available: activeInfo?.has_dml_report },
  ]

  // Render source with line numbers
  const renderSource = (text, emptyMsg) => {
    if (!text) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{emptyMsg}</div>
    const lines = text.split('\n')
    return (
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg-primary)' }}>
        <pre style={{
          margin: 0, padding: '12px 0', fontSize: 11, fontFamily: 'monospace',
          lineHeight: '18px', whiteSpace: 'pre', color: 'var(--text-primary)',
        }}>
          {lines.map((line, i) => (
            <div key={i} style={{ display: 'flex', minHeight: 18 }}>
              <span style={{
                display: 'inline-block', width: 50, textAlign: 'right', paddingRight: 12,
                color: 'var(--text-muted)', fontSize: 10, userSelect: 'none', flexShrink: 0,
                borderRight: '1px solid var(--border-color)', marginRight: 12,
              }}>{i + 1}</span>
              <span style={{ whiteSpace: 'pre' }}>{line}</span>
            </div>
          ))}
        </pre>
      </div>
    )
  }

  // Render sub-tab content
  const renderSubTabContent = () => {
    if (!activeEmitter) {
      return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Select a program to view its V6 output.</div>
    }
    switch (activeSubTab) {
      case 'source':
        return renderSource(activeEmitter.output_source, 'No V6 source output available for this program.')
      case 'changes':
        return renderSource(activeEmitter.change_report, 'No changes applied to this program.')
      case 'jcl':
        return renderSource(activeEmitter.jcl_report, 'No JCL changes required for this program.')
      case 'dml':
        return renderSource(activeEmitter.dml_report, 'No DML migration data available for this program.')
      default:
        return null
    }
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* KPI Strip */}
      <div style={{
        display: 'flex', gap: 0, padding: '10px 16px',
        borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
      }}>
        {[
          { label: 'Programs',       value: kpis.total,             color: '#388bfd' },
          { label: 'Changed',        value: kpis.changed,           color: '#3fb950' },
          { label: 'Unchanged',      value: kpis.unchanged,         color: '#6b7280' },
          { label: 'Lines Changed',  value: kpis.totalLinesChanged, color: '#f59e0b' },
        ].map(k => (
          <div key={k.label} style={{
            flex: 1, textAlign: 'center', padding: '4px 8px',
            borderRight: '1px solid var(--border-color)',
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Main content: sidebar + content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left sidebar — program file list */}
        <div style={{
          width: 220, borderRight: '1px solid var(--border-color)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          background: 'var(--bg-secondary)',
        }}>
          <div style={{
            padding: '8px 12px', fontSize: 11, fontWeight: 600,
            color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5,
            borderBottom: '1px solid var(--border-color)',
          }}>Program Files</div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {programList.map(prog => {
              const isSelected = prog.stem === activeStem
              return (
                <div
                  key={prog.stem}
                  onClick={() => { setSelectedProgram(prog.stem); setActiveSubTab('source') }}
                  style={{
                    padding: '8px 12px', cursor: 'pointer',
                    borderLeft: isSelected ? '3px solid #388bfd' : '3px solid transparent',
                    background: isSelected ? 'var(--bg-tertiary)' : 'transparent',
                    borderBottom: '1px solid var(--border-color)',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-tertiary)' }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
                >
                  <div style={{
                    fontSize: 12, fontFamily: 'monospace', fontWeight: isSelected ? 600 : 400,
                    color: 'var(--text-primary)', marginBottom: 3,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{prog.filename || prog.stem}</div>
                  <div style={{ fontSize: 10 }}>
                    {prog.is_unchanged ? (
                      <span style={{
                        color: '#6b7280', background: 'rgba(107,114,128,0.1)',
                        padding: '1px 6px', borderRadius: 8,
                      }}>unchanged</span>
                    ) : (
                      <span style={{
                        color: '#388bfd', background: 'rgba(56,139,253,0.1)',
                        padding: '1px 6px', borderRadius: 8,
                      }}>{prog.lines_changed} lines changed</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          {/* Download buttons */}
          <div style={{
            padding: '8px 10px', borderTop: '1px solid var(--border-color)',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            <button
              disabled={!activeEmitter?.output_source}
              onClick={() => {
                if (!activeEmitter?.output_source) return
                const fname = (activeInfo?.filename || activeStem || 'v6_source') + (activeInfo?.filename ? '' : '.cbl')
                const blob = new Blob([activeEmitter.output_source], { type: 'text/plain' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url; a.download = fname; a.click()
                URL.revokeObjectURL(url)
              }}
              style={{
                width: '100%', padding: '7px 0', border: 'none', borderRadius: 6, cursor: activeEmitter?.output_source ? 'pointer' : 'default',
                fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: activeEmitter?.output_source ? '#0ea5e9' : 'var(--bg-tertiary)',
                color: activeEmitter?.output_source ? '#fff' : 'var(--text-muted)',
                opacity: activeEmitter?.output_source ? 1 : 0.5,
              }}
            ><Download size={13} /> Export V6 Source</button>
            <button
              onClick={async () => {
                if (!compatibilityResults) return
                const zip = new JSZip()
                Object.entries(compatibilityResults).forEach(([stem, prog]) => {
                  const src = prog?.emitter?.output_source
                  if (src) {
                    const fname = (v6Manifest?.[stem]?.filename) || `${stem}.cbl`
                    zip.file(fname, src)
                  }
                })
                const blob = await zip.generateAsync({ type: 'blob' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url; a.download = 'v6_output.zip'; a.click()
                URL.revokeObjectURL(url)
              }}
              style={{
                width: '100%', padding: '7px 0', border: 'none', borderRadius: 6, cursor: 'pointer',
                fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: '#0ea5e9', color: '#fff',
              }}
            ><Download size={13} /> Download ZIP</button>
          </div>
        </div>

        {/* Right panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header bar for selected program */}
          {activeInfo && (
            <div style={{
              padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12,
              borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                {activeInfo.filename || activeStem}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {activeInfo.original_lines} → {activeInfo.output_lines} lines
              </span>
              {!activeInfo.is_unchanged && (
                <span style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 10,
                  background: 'rgba(63,185,80,0.15)', color: '#3fb950', fontWeight: 600,
                }}>{activeInfo.lines_changed} changed</span>
              )}
              {activeInfo.is_unchanged && (
                <span style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 10,
                  background: 'rgba(107,114,128,0.1)', color: '#6b7280', fontWeight: 600,
                }}>unchanged</span>
              )}
            </div>
          )}

          {/* Sub-tab bar */}
          <div style={{
            display: 'flex', gap: 0, padding: '6px 16px',
            borderBottom: '1px solid var(--border-color)',
          }}>
            {subTabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveSubTab(t.key)}
                disabled={!t.available && t.key !== 'source'}
                style={{
                  padding: '5px 14px', border: 'none', cursor: t.available || t.key === 'source' ? 'pointer' : 'default',
                  fontSize: 12, fontWeight: activeSubTab === t.key ? 600 : 400,
                  background: activeSubTab === t.key ? 'var(--bg-tertiary)' : 'transparent',
                  color: activeSubTab === t.key ? 'var(--text-primary)'
                    : (t.available || t.key === 'source') ? 'var(--text-muted)' : 'var(--text-muted)',
                  opacity: (t.available || t.key === 'source') ? 1 : 0.4,
                  borderRadius: 6,
                }}
              >{t.label}</button>
            ))}
          </div>

          {/* Sub-tab content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {renderSubTabContent()}
          </div>
        </div>
      </div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Assessment
// ═══════════════════════════════════════════════════════════════════════════════

function AssessmentTab({ assessmentFull, programRisks }) {
  if (!assessmentFull) return <NotAvailable message="IDMS Assessment has not run yet." />

  // assessment_full.json: { readiness: { score, ... }, program_risks: [...], migration_sequence: [...], ... }
  const readinessObj = assessmentFull.readiness || {}
  const readiness    = readinessObj.score ?? readinessObj.readiness_score ?? assessmentFull.readiness_score ?? 0

  // Build categories from assessment_full fields
  const categories = {}
  if (assessmentFull.coupling) categories.coupling = assessmentFull.coupling
  if (readinessObj.data_complexity != null) categories.data_complexity = { score: readinessObj.data_complexity }
  if (readinessObj.code_complexity != null) categories.code_complexity = { score: readinessObj.code_complexity }

  // program_risks: may be array in assessment_full or dict keyed by program in program_risks.json
  let risks = []
  const assessRisks = assessmentFull.program_risks
  if (Array.isArray(assessRisks)) {
    risks = assessRisks
  } else if (assessRisks && typeof assessRisks === 'object') {
    risks = Object.entries(assessRisks).map(([k, v]) => ({ program: k, ...(typeof v === 'object' ? v : { risk_level: v }) }))
  }
  // Also merge programRisks.json if available (dict keyed by program)
  if (programRisks && typeof programRisks === 'object' && !Array.isArray(programRisks) && risks.length === 0) {
    risks = Object.entries(programRisks).map(([k, v]) => ({ program: k, ...(typeof v === 'object' ? v : { risk_level: v }) }))
  }

  const migration = assessmentFull.migration_sequence || []

  // Record coverage
  const recCov = assessmentFull.record_coverage || {}
  const recordsUsed  = recCov.accessed || recCov.records_accessed || 0
  const recordsTotal = recCov.total || recCov.records_total || 0
  const coverage     = recordsTotal > 0 ? Math.round((recordsUsed / recordsTotal) * 100) : 0

  return (
    <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
      {/* Readiness banner */}
      <div style={{
        padding: '20px 24px', borderRadius: 10, marginBottom: 20,
        background: readiness >= 80 ? 'rgba(63,185,80,0.08)' : readiness >= 50 ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)',
        border: `1px solid ${readiness >= 80 ? 'rgba(63,185,80,0.2)' : readiness >= 50 ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)'}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Migration Readiness</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: readiness >= 80 ? '#3fb950' : readiness >= 50 ? '#f59e0b' : '#ef4444' }}>
              {readiness}%
            </div>
          </div>
          {readinessObj.effort_estimate && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Estimated Effort</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{readinessObj.effort_estimate}</div>
            </div>
          )}
        </div>
        {/* Progress bar */}
        <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-tertiary)', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4, transition: 'width 0.6s',
            width: `${readiness}%`,
            background: readiness >= 80 ? '#3fb950' : readiness >= 50 ? '#f59e0b' : '#ef4444',
          }} />
        </div>
      </div>

      {/* Category cards */}
      {Object.keys(categories).length > 0 && (
        <>
          <SectionHeader>Category Breakdown</SectionHeader>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
            {Object.entries(categories).map(([cat, data]) => {
              const score = typeof data === 'number' ? data : (data.score || 0)
              const details = typeof data === 'object' ? data : null
              return (
                <div key={cat} style={{
                  padding: 16, borderRadius: 8, background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                      {cat.replace(/_/g, ' ')}
                    </span>
                    <ScoreBadge score={score} />
                  </div>
                  {details?.breakdown && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {Object.entries(details.breakdown).map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                          <span>{k.replace(/_/g, ' ')}</span>
                          <span style={{ fontWeight: 500 }}>{typeof v === 'number' ? v : String(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Record coverage */}
      {recordsTotal > 0 && (
        <>
          <SectionHeader>Record Coverage</SectionHeader>
          <div style={{
            padding: 16, borderRadius: 8, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)', marginBottom: 24,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
              <span>{recordsUsed} of {recordsTotal} records accessed</span>
              <span style={{ fontWeight: 600 }}>{coverage}%</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-tertiary)', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 4,
                width: `${coverage}%`, background: '#3b82f6',
              }} />
            </div>
          </div>
        </>
      )}

      {/* Risk cards grid */}
      {risks.length > 0 && (
        <>
          <SectionHeader>Program Risk Assessment</SectionHeader>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10, marginBottom: 24 }}>
            {risks.map((r, i) => {
              const risk = (r.risk_level || r.risk || 'medium').toLowerCase()
              const riskColor = risk === 'high' ? '#ef4444' : risk === 'low' ? '#3fb950' : '#f59e0b'
              return (
                <div key={i} style={{
                  padding: 12, borderRadius: 8, background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderLeft: `3px solid ${riskColor}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                      {r.program || r.filename || `Program ${i + 1}`}
                    </span>
                    <span style={{
                      fontSize: 10, padding: '1px 8px', borderRadius: 10, fontWeight: 600,
                      color: riskColor,
                      background: risk === 'high' ? 'rgba(239,68,68,0.12)' : risk === 'low' ? 'rgba(63,185,80,0.12)' : 'rgba(245,158,11,0.12)',
                    }}>{risk}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {r.type && <span>Type: {r.type}</span>}
                    {r.dml_count != null && <span>DML: {r.dml_count}</span>}
                    {r.findings_count != null && <span>Findings: {r.findings_count}</span>}
                    {r.complexity && <span>Complexity: {r.complexity}</span>}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Migration sequence */}
      {migration.length > 0 && (
        <>
          <SectionHeader>Migration Sequence</SectionHeader>
          <div style={{
            padding: 16, borderRadius: 8, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
          }}>
            {migration.map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '8px 0', borderBottom: i < migration.length - 1 ? '1px solid var(--border-color)' : 'none',
              }}>
                <span style={{
                  width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(59,130,246,0.12)', color: '#3b82f6', fontWeight: 700, fontSize: 12, flexShrink: 0,
                }}>{i + 1}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                    {typeof item === 'string' ? item : (item.program || item.name)}
                  </div>
                  {typeof item === 'object' && item.rationale && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.rationale}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: SQL Generation
// ═══════════════════════════════════════════════════════════════════════════════

function SqlTab({ sqlTables, sqlProcedures, dialect, onDialectChange, isRegenerating }) {
  const [selectedItem, setSelectedItem] = useState(null)
  const [expandedSections, setExpandedSections] = useState({ tables: true, procedures: true })

  if (!sqlTables && !sqlProcedures) return <NotAvailable message="IDMS SQL Generation has not run yet." />

  // sql_tables.json and sql_procedures.json are flat arrays
  const tables     = Array.isArray(sqlTables) ? sqlTables : (sqlTables?.tables || [])
  const procedures = Array.isArray(sqlProcedures) ? sqlProcedures : (sqlProcedures?.procedures || [])

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }))

  // Current SQL to display
  const currentSql = selectedItem?.ddl || selectedItem?.sql || selectedItem?.body || ''
  const currentName = selectedItem?.name || selectedItem?.table_name || 'Select an item'

  const DIALECT_OPTIONS = ['SQL Server', 'DB2']
  const currentDialect = dialect || 'SQL Server'

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Target Database header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 16px', borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)',
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Target Database:
        </span>
        <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
          {DIALECT_OPTIONS.map(d => (
            <button
              key={d}
              disabled={isRegenerating}
              onClick={() => { if (d !== currentDialect && onDialectChange) onDialectChange(d) }}
              style={{
                padding: '4px 14px', border: 'none', cursor: isRegenerating ? 'wait' : 'pointer',
                fontSize: 12, fontWeight: d === currentDialect ? 600 : 400,
                background: d === currentDialect ? '#0ea5e9' : 'var(--bg-tertiary)',
                color: d === currentDialect ? '#fff' : 'var(--text-secondary)',
              }}
            >{d}</button>
          ))}
        </div>
        {isRegenerating && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#f59e0b' }}>
            <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Regenerating...
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
          {tables.length} tables | {procedures.length} stored procedures
        </span>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left: Tree view */}
      <div style={{
        width: 260, borderRight: '1px solid var(--border-color)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        flexShrink: 0, background: 'var(--bg-secondary)',
      }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Tables section */}
          <button
            onClick={() => toggleSection('tables')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              width: '100%', padding: '10px 12px', border: 'none', cursor: 'pointer',
              background: 'transparent', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600,
            }}
          >
            {expandedSections.tables ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <Database size={14} style={{ color: '#3b82f6' }} />
            Tables ({tables.length})
          </button>
          {expandedSections.tables && tables.map((t, i) => (
            <button
              key={i}
              onClick={() => setSelectedItem(t)}
              style={{
                display: 'block', width: '100%', padding: '6px 12px 6px 36px',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                fontSize: 12, fontFamily: 'monospace',
                background: selectedItem === t ? 'var(--bg-tertiary)' : 'transparent',
                color: selectedItem === t ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: selectedItem === t ? 600 : 400,
              }}
            >
              {t.name || t.table_name || `Table ${i + 1}`}
            </button>
          ))}

          {/* Procedures section */}
          <button
            onClick={() => toggleSection('procedures')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              width: '100%', padding: '10px 12px', border: 'none', cursor: 'pointer',
              background: 'transparent', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600,
            }}
          >
            {expandedSections.procedures ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <Code2 size={14} style={{ color: '#10b981' }} />
            Procedures ({procedures.length})
          </button>
          {expandedSections.procedures && procedures.map((p, i) => (
            <button
              key={i}
              onClick={() => setSelectedItem(p)}
              style={{
                display: 'block', width: '100%', padding: '6px 12px 6px 36px',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                fontSize: 12, fontFamily: 'monospace',
                background: selectedItem === p ? 'var(--bg-tertiary)' : 'transparent',
                color: selectedItem === p ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: selectedItem === p ? 600 : 400,
              }}
            >
              {p.name || p.procedure_name || `Proc ${i + 1}`}
            </button>
          ))}
        </div>
        {/* Download buttons */}
        <div style={{
          padding: '8px 10px', borderTop: '1px solid var(--border-color)',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <button
            disabled={!currentSql}
            onClick={() => {
              if (!currentSql) return
              const fname = (currentName || 'script').replace(/\s+/g, '_') + '.sql'
              const blob = new Blob([currentSql], { type: 'text/plain' })
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url; a.download = fname; a.click()
              URL.revokeObjectURL(url)
            }}
            style={{
              width: '100%', padding: '7px 0', border: 'none', borderRadius: 6,
              cursor: currentSql ? 'pointer' : 'default',
              fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: currentSql ? '#0ea5e9' : 'var(--bg-tertiary)',
              color: currentSql ? '#fff' : 'var(--text-muted)',
              opacity: currentSql ? 1 : 0.5,
            }}
          ><Download size={13} /> Download Current</button>
          <button
            onClick={async () => {
              const zip = new JSZip()
              tables.forEach(t => {
                const sql = t.ddl || t.sql || ''
                if (sql) zip.file((t.name || t.table_name || 'table') + '.sql', sql)
              })
              procedures.forEach(p => {
                const sql = p.ddl || p.sql || p.body || ''
                if (sql) zip.file((p.name || p.procedure_name || 'proc') + '.sql', sql)
              })
              const blob = await zip.generateAsync({ type: 'blob' })
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url; a.download = 'sql_generation.zip'; a.click()
              URL.revokeObjectURL(url)
            }}
            style={{
              width: '100%', padding: '7px 0', border: 'none', borderRadius: 6, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: '#0ea5e9', color: '#fff',
            }}
          ><Download size={13} /> Download All (ZIP)</button>
        </div>
      </div>

      {/* Right: SQL viewer */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{
          padding: '8px 16px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: 'var(--text-primary)' }}>
            {currentName}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => { if (currentSql) navigator.clipboard.writeText(currentSql) }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-color)',
                background: 'var(--bg-tertiary)', cursor: 'pointer',
                color: 'var(--text-secondary)', fontSize: 11,
              }}
            >
              <Copy size={12} /> Copy
            </button>
          </div>
        </div>

        {/* SQL content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
          {currentSql ? (
            <pre style={{
              margin: 0, padding: 16, fontSize: 12, fontFamily: 'monospace',
              color: '#93c5fd', lineHeight: 1.6, whiteSpace: 'pre',
              background: 'var(--bg-primary)',
            }}>
              {currentSql}
            </pre>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Select a table or procedure from the tree to view its SQL.
            </div>
          )}
        </div>
      </div>
      </div>{/* end flex row */}
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN: IdmsAnalyzeView
// ═══════════════════════════════════════════════════════════════════════════════

export default function IdmsAnalyzeView({
  project,
  onBack,
  onBackToProjects,
  onOpenSettings,
  onOpenHelp,
  userProfile = {},
}) {
  const [activeTab, setActiveTab]     = useState('inventory')
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)
  const [initialCopybook, setInitialCopybook] = useState(null)
  const [sqlDialect, setSqlDialect]           = useState('SQL Server')
  const [isRegeneratingSql, setIsRegeneratingSql] = useState(false)

  // Job IDs for each IDMS pipeline node
  const [jobIds, setJobIds] = useState({
    analysis:         null,
    compatibility:    null,
    dataArchitecture: null,
    assessment:       null,
    sqlGeneration:    null,
    ingest:           null,
  })

  // Artifact data (loaded lazily per tab)
  const [data, setData] = useState({
    programResults:    null,
    dmlOperations:     null,
    schemaInventory:   null,
    copybooks:         null,
    treeSitterResults: null,
    findings:          null,
    transformChanges:  null,
    v6Manifest:           null,
    compatibilityResults: null,
    inferredSchema:    null,
    analysisGraph:     null,
    assessmentFull:    null,
    programRisks:      null,
    sqlTables:         null,
    sqlProcedures:     null,
  })

  const loadedTabs = useRef(new Set())

  const projectName = project?.name || 'Unknown'
  const accountId   = project?.account || ''

  // ── Step 1: Discover job IDs ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    setLoading(true)

    const discover = async () => {
      try {
        const [analysis, compatibility, versionUpgrade, dataArch, assessment, sqlGen] = await Promise.all([
          fetchLatestJob('idmsanalysis',         accountId, projectName),
          fetchLatestJob('idmscompatibility',     accountId, projectName),
          fetchLatestJob('cobolversionupgrade',   accountId, projectName),
          fetchLatestJob('idmsdataarchitecture',  accountId, projectName),
          fetchLatestJob('idmsassessment',        accountId, projectName),
          fetchLatestJob('idmssqlgeneration',     accountId, projectName),
        ])

        if (cancelled) return

        // Try to discover ingest job ID for AI file content fetching
        let ingest = null
        try {
          const jobsResp = await fetchJson(
            `${API_BASE}/jobs/by-app/${encodeURIComponent(accountId)}/${encodeURIComponent(projectName)}`
          )
          if (Array.isArray(jobsResp)) {
            const ingestJob = jobsResp.find(j =>
              (j.flow_type === 'ingest' || j.job_type === 'ingest' || j.flow_type === 'catalogingest') &&
              (j.status === 'SUCCEEDED' || j.status === 'completed' || j.status === 'COMPLETED')
            )
            ingest = ingestJob?.job_id || ingestJob?.id || null
          }
        } catch (e) {
          console.warn('[IdmsAnalyze] Could not discover ingest job:', e.message)
        }

        console.log('[IdmsAnalyze] Job IDs discovered:', { analysis, compatibility, versionUpgrade, dataArch, assessment, sqlGen, ingest })

        if (!analysis) {
          setError('No IDMS Analysis data found for this project.')
          setLoading(false)
          return
        }

        setJobIds({
          analysis,
          compatibility,
          versionUpgrade,
          dataArchitecture: dataArch,
          assessment,
          sqlGeneration: sqlGen,
          ingest,
        })
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    discover()
    return () => { cancelled = true }
  }, [accountId, projectName])

  // ── Step 2: Load artifacts for active tab ──────────────────────────────────
  useEffect(() => {
    if (!jobIds.analysis || loadedTabs.current.has(activeTab)) return

    const load = async () => {
      loadedTabs.current.add(activeTab)

      try {
        switch (activeTab) {
          case 'inventory': {
            const [programResults, dmlOperations, copybooks] = await Promise.all([
              fetchArtifact('idmsanalysis', jobIds.analysis, 'program_results.json'),
              fetchArtifact('idmsanalysis', jobIds.analysis, 'dml_operations.json'),
              fetchArtifact('idmsanalysis', jobIds.analysis, 'copybooks.json'),
            ])
            setData(prev => ({ ...prev, programResults, dmlOperations, copybooks }))
            break
          }
          case 'dml': {
            if (!data.dmlOperations) {
              const dmlOperations = await fetchArtifact('idmsanalysis', jobIds.analysis, 'dml_operations.json')
              setData(prev => ({ ...prev, dmlOperations }))
            }
            break
          }
          case 'schema': {
            const fetches = []
            if (!data.schemaInventory) {
              fetches.push(
                fetchArtifact('idmsanalysis', jobIds.analysis, 'schema_inventory.json')
                  .then(d => ({ schemaInventory: d }))
              )
            }
            if (!data.inferredSchema && jobIds.dataArchitecture) {
              fetches.push(
                fetchArtifact('idmsdataarchitecture', jobIds.dataArchitecture, 'inferred_schema.json')
                  .then(d => ({ inferredSchema: d }))
              )
            }
            if (!data.dmlOperations) {
              fetches.push(
                fetchArtifact('idmsanalysis', jobIds.analysis, 'dml_operations.json')
                  .then(d => ({ dmlOperations: d }))
              )
            }
            if (fetches.length) {
              const results = await Promise.all(fetches)
              const merged = Object.assign({}, ...results)
              setData(prev => ({ ...prev, ...merged }))
            }
            break
          }
          case 'architecture': {
            const [inferredSchema, analysisGraph] = await Promise.all([
              fetchArtifact('idmsdataarchitecture', jobIds.dataArchitecture, 'inferred_schema.json'),
              fetchArtifact('idmsdataarchitecture', jobIds.dataArchitecture, 'analysis_graph.json'),
            ])
            setData(prev => ({ ...prev, inferredSchema, analysisGraph }))
            break
          }
          case 'copybooks': {
            const fetches = []
            if (!data.copybooks) {
              fetches.push(fetchArtifact('idmsanalysis', jobIds.analysis, 'copybooks.json').then(d => ({ copybooks: d })))
            }
            if (!data.schemaInventory) {
              fetches.push(fetchArtifact('idmsanalysis', jobIds.analysis, 'schema_inventory.json').then(d => ({ schemaInventory: d })).catch(() => ({ schemaInventory: null })))
            }
            if (fetches.length) {
              const results = await Promise.all(fetches)
              const merged = Object.assign({}, ...results)
              setData(prev => ({ ...prev, ...merged }))
            }
            break
          }
          case 'compatibility': {
            const findings = await fetchArtifact('idmscompatibility', jobIds.compatibility, 'cmpr2_findings.json')
            // Transform changes come from the Version Upgrade node (if run)
            let transformChanges = null
            if (jobIds.versionUpgrade) {
              try {
                transformChanges = await fetchArtifact('cobolversionupgrade', jobIds.versionUpgrade, 'transform_changes.json')
              } catch (e) { /* version upgrade may not have run yet */ }
            }
            setData(prev => ({ ...prev, findings, transformChanges }))
            break
          }
          case 'assessment': {
            const [assessmentFull, programRisks] = await Promise.all([
              fetchArtifact('idmsassessment', jobIds.assessment, 'assessment_full.json'),
              fetchArtifact('idmsassessment', jobIds.assessment, 'program_risks.json'),
            ])
            setData(prev => ({ ...prev, assessmentFull, programRisks }))
            break
          }
          case 'v6output': {
            const [v6Manifest, compatibilityResults] = await Promise.all([
              fetchArtifact('cobolversionupgrade', jobIds.versionUpgrade, 'v6_output_manifest.json'),
              fetchArtifact('cobolversionupgrade', jobIds.versionUpgrade, 'upgrade_results.json'),
            ])
            setData(prev => ({ ...prev, v6Manifest, compatibilityResults }))
            break
          }
          case 'sql': {
            const [sqlTables, sqlProcedures] = await Promise.all([
              fetchArtifact('idmssqlgeneration', jobIds.sqlGeneration, 'sql_tables.json'),
              fetchArtifact('idmssqlgeneration', jobIds.sqlGeneration, 'sql_procedures.json'),
            ])
            setData(prev => ({ ...prev, sqlTables, sqlProcedures }))
            break
          }
        }
      } catch (err) {
        console.error('[IdmsAnalyze] Failed to load tab data:', activeTab, err)
      }
    }

    load()
  }, [activeTab, jobIds, data])

  // ── SQL Dialect change handler ──────────────────────────────────────────────
  const handleDialectChange = useCallback(async (newDialect) => {
    if (isRegeneratingSql || !jobIds.analysis || !jobIds.dataArchitecture) return
    setSqlDialect(newDialect)
    setIsRegeneratingSql(true)

    try {
      // Trigger new SQL generation job with selected dialect
      const resp = await fetch(`${API_BASE}/idmssqlgeneration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          application_name: projectName,
          idms_analysis_job_id: jobIds.analysis,
          idms_data_architecture_job_id: jobIds.dataArchitecture,
          dialect: newDialect,
        }),
      })
      const result = await resp.json()
      const newJobId = result.job_id

      if (!newJobId) throw new Error('No job_id returned')

      // Poll for completion
      let status = 'running'
      while (status === 'running' || status === 'pending') {
        await new Promise(r => setTimeout(r, 1500))
        const statusResp = await fetch(`${API_BASE}/idmssqlgeneration/${newJobId}/status`, {
          headers: getAuthHeaders(),
        })
        const statusData = await statusResp.json()
        status = (statusData.status || '').toLowerCase()
      }

      if (status !== 'completed') throw new Error(`SQL generation failed: ${status}`)

      // Reload results from new job
      const [sqlTables, sqlProcedures] = await Promise.all([
        fetchArtifact('idmssqlgeneration', newJobId, 'sql_tables.json'),
        fetchArtifact('idmssqlgeneration', newJobId, 'sql_procedures.json'),
      ])
      setData(prev => ({ ...prev, sqlTables, sqlProcedures }))
      setJobIds(prev => ({ ...prev, sqlGeneration: newJobId }))
    } catch (err) {
      console.error('[IdmsAnalyze] SQL dialect regeneration failed:', err)
    } finally {
      setIsRegeneratingSql(false)
    }
  }, [isRegeneratingSql, jobIds, projectName])

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="ga-view" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={32} className="spin" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 14 }}>Discovering IDMS analysis data...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="ga-view" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <AlertTriangle size={32} style={{ marginBottom: 12, color: '#f59e0b' }} />
          <div style={{ fontSize: 14 }}>{error}</div>
          <button onClick={onBack} style={{
            marginTop: 16, padding: '8px 16px', borderRadius: 6,
            border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)',
            color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13,
          }}>Go Back</button>
        </div>
      </div>
    )
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'inventory':
        return <InventoryTab programResults={data.programResults} dmlOps={data.dmlOperations} copybooks={data.copybooks} project={project} ingestJobId={jobIds.ingest} />
      case 'dml':
        return <DmlTab dmlOps={data.dmlOperations} copybooks={data.copybooks} programResults={data.programResults} onNavigateToCopybook={(name) => { setInitialCopybook(name); setActiveTab('copybooks') }} />
      case 'schema':
        return <SchemaTab schemaInventory={data.schemaInventory} inferredSchema={data.inferredSchema} dmlOps={data.dmlOperations} />
      case 'architecture':
        return <DataArchitectureTab inferredSchema={data.inferredSchema} analysisGraph={data.analysisGraph} />
      case 'copybooks':
        return <CopybooksTab copybooks={data.copybooks} schemaInventory={data.schemaInventory} initialCopybook={initialCopybook} />
      case 'compatibility':
        return <CompatibilityTab findings={data.findings} transformChanges={data.transformChanges} />
      case 'assessment':
        return <AssessmentTab assessmentFull={data.assessmentFull} programRisks={data.programRisks} />
      case 'v6output':
        return <V6OutputTab v6Manifest={data.v6Manifest} compatibilityResults={data.compatibilityResults} />
      case 'sql':
        return <SqlTab sqlTables={data.sqlTables} sqlProcedures={data.sqlProcedures} dialect={sqlDialect} onDialectChange={handleDialectChange} isRegenerating={isRegeneratingSql} />
      default:
        return null
    }
  }

  return (
    <div className="ga-view">

      {/* ── Header — same shell as RunsView / GuidedAnalyzeView ───────────── */}
      <div className="runs-header-bar">

        {/* Left: back + breadcrumb */}
        <div className="runs-header-left">
          <button className="runs-back-btn" onClick={onBack}>
            <ArrowLeft size={15} />
            <span>Projects</span>
          </button>
          <div className="runs-breadcrumb">
            <span className="runs-bc-project">{projectName}</span>
            <ChevronRight size={14} className="runs-bc-sep" />
            <span className="runs-bc-page">IDMS Analyze</span>
          </div>
        </div>

        {/* Center: mode-switcher nav */}
        <div className="runs-header-center">
          <div className="mode-switcher">
            <button className="mode-btn" onClick={onBackToProjects || onBack} title="Projects">
              <Home size={16} />
              <span>Home</span>
            </button>
            <button className="mode-btn active" title="IDMS Analyze">
              <BarChart2 size={16} />
              <span>Analyze</span>
            </button>
          </div>
        </div>

        {/* Right: settings + user */}
        <div className="runs-header-right">
          <button className="icon-btn" onClick={onOpenSettings} title="Settings">
            <Settings size={16} />
          </button>
          <button className="icon-btn" onClick={onOpenHelp} title="Help">
            <HelpCircle size={16} />
          </button>
          <div className="runs-user-avatar" title={userProfile?.display_name || 'User'}>
            <User size={14} />
          </div>
        </div>
      </div>

      {/* ── Tab Navigation ────────────────────────────────────────────────── */}
      <div className="ga-nav-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.key}
              className={`ga-nav-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.key); setInitialCopybook(null) }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon size={14} />
                {tab.label}
              </span>
            </button>
          )
        })}
      </div>

      {/* ── Tab Content ───────────────────────────────────────────────────── */}
      <div className="ga-workspace" style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        <div className="ga-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {renderTabContent()}
        </div>
      </div>
    </div>
  )
}
